import React, { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { useForm } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import axios from 'axios'
import 'katex/dist/katex.min.css'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { selectRootUser } from '../../rootSlice'
import {
  callOpenAIChat,
  rateOpenAIChat,
  fetchOpenAIAccount,
  fetchOpenAIChatHistory,
  fetchOpenAIChatConversation,
  selectOpenAIAccount,
  selectDashboardConfig,
  selectOpenAIChatHistory,
  selectOpenAIChatHistoryStatus,
  selectOpenAIAccountStatus,
  createOpenAIAccount,
  updateOpenAIAccount,
  selectOpenAIChatStatus,
  // Agent mode
  toggleAgentMode,
  selectAgentModeEnabled,
  selectEffortLevel,
  setEffortLevel,
  selectKnowledgeSources,
  selectGeneratedPdfs,
  pruneInsightBuckets,
  selectActiveChatIds,
  selectAgentCreditSummary,
  selectAgentModel,
  selectAgentModelCatalog,
  setAgentModel,
  selectImageModel,
  setImageModel,
  askAgentForInsight,
  selectAgentSteps,
  selectAgentStatus,
  setAgentStatus,
  clearAgentSteps,
  resetInsightForSourcePage,
  markChatLoading,
  markChatIdle,
  upsertChatHistoryItem,
  selectInsightResponse,
  selectInsightStatus,
  selectAgentMemoryUpdate,
  selectAgentNarrations,
  selectAgentAvailableTools,
  // Chat preferences for seamless resumption
  fetchChatPreferences,
  updateChatPreferences,
  fetchAgentSettings,
  fetchAgentModelCatalog,
  markSessionInterrupted,
  selectChatPreferencesStatus,
  selectLastActiveChatId,
  selectAutoLoadEnabled,
  selectInterruptedSessions,
  // Dynamic question suggestions
  fetchRelatedQuestions,
  selectRelatedQuestions,
  selectRelatedQuestionsStatus,
  fetchScheduledTasks,
  setSelectedTaskId,
  selectSelectedTaskId,
  selectScheduledTasks,
  suggestScheduledTask,
  dismissTaskSuggestion,
  clearTaskSuggestions,
  selectTaskSuggestions,
  clearTaskIntentSynthesis,
  // Task Library "Chat Now" deep-link
  fetchPreBuiltTaskById,
  setActivePreBuiltTaskContext,
  clearActivePreBuiltTaskContext,
  selectActivePreBuiltTaskContext,
  insufficientAiCreditsMessage,
} from '../../features/openai/openaiSlice'
import { isDeepDiveLevel } from '../../features/openai/agentModels'
import { getSecretId } from '../Integrations/channels/generic/helpers'
import { isTeamOwner as checkIsTeamOwner } from '../Integrations/isTeamOwner'
import { getOwnerId, resolveIntegrationsOwnerId, randomString, getAxiosDefaultConfig } from '../../modules/utils'
import { openAIUrl } from 'urls'
import { fetchTeamAccounts, selectTeamAccounts, selectTeamAccountsStatus } from '../Settings/settingsSlice'
import ChatGPTLogo from '../../assets/images/logos/chatgpt.svg'
import PayHelmLogo from '../../assets/images/payhelm-logo-blue.png'
import { BiLoaderAlt, BiSolidFilePdf, BiDownload } from 'react-icons/bi'
import { useWindowSize } from 'react-use'
import ReactTooltip from 'react-tooltip'
import { replaceLinks } from './helpers'
import { Button, Modal, Form, Dropdown, DropdownButton } from 'react-bootstrap'
import MyCheckbox from '../../features/forms/MyCheckbox'
import { connect } from 'react-redux'
import { changeApplication } from 'actions/rootActions'
import ReportDataTable from './components/ReportDataTable'
import AIMetricCards from './components/AIMetricCards'
import AIInsightCards from './components/AIInsightCards'
import AIChart from './components/AIChart'
import SupportVideos from './components/SupportVideos'
import { saveCustomReport } from 'actions/customReportsActions'
import { LinkContainer } from 'react-router-bootstrap'
import { toast } from 'react-toastify'
import VoiceInput from './components/VoiceInput'
import VoiceOutput from './components/VoiceOutput'
import MessageCreditCost from '../../sharedComponents/AIChatBox/MessageCreditCost'
import AgentChainOfThought, { AnswerCard } from './components/AgentChainOfThought'
import SubmarineIcon from './components/SubmarineIcon'
import { hasSubstantiveAgentSteps } from './utils/agentPlanSteps'
import {
  AGENT_NO_ANSWER_NOTICE,
  AGENT_WORKING_PLACEHOLDER,
  isTerminalAgentStatus,
  resolveCompletedAgentContent,
  shouldRenderRestoredAssistantTurn,
  toDisplayAgentStatus,
} from './utils/agentTurnDisplay'
import AgentResultsPanel from './components/AgentResultsPanel'
import AgentReportPanel from './components/AgentReportPanel'
import AgentReportCard from './components/AgentReportCard'
import { extractAgentReportCards } from './agentReportCards'
import type { AgentReportCard as AgentReportCardData } from './agentReportCards'
import AgentDataPanel from './components/AgentDataPanel'
import ChatHistoryMainView from './components/ChatHistoryMainView'
import CustomToolsMainView from './components/CustomToolsMainView'
import ToolAccessMainView from './components/ToolAccessMainView'
import ScheduledTaskDetail from './components/ScheduledTaskDetail'
import TaskConfigPanel from './components/TaskConfigPanel'
import TaskSuggestionCard from './components/TaskSuggestionCard'
import AgentSettingsMenu from './components/AgentSettingsMenu'
import PasteClipChip from './components/PasteClipChip'
import FileTypeIcon from './components/FileTypeIcon'
import { Sheet, SheetContent, SheetTitle } from '@/features/shadcn/ui/sheet'
import { COMPACT_BREAKPOINT, MOBILE_BREAKPOINT } from '@/features/hooks/use-mobile'
import { initialState as dashboardInitialState } from '../../reducers/dashboardReducer'
import { initialState as rangeFiltersInitialState } from '../../reducers/rangeFiltersReducer'
import { initialState as keywordFiltersInitialState } from '../../reducers/keywordFiltersReducer'
import { setReportData, getReport, setTempReportConfig } from 'actions/reportsActions'
import Footer from 'sharedComponents/Footer'
import ScheduleReportModal from '../Reports/components/ScheduleModal'
import { addOnOptions } from '../Plan/planConfig'
import { setDoneAction } from 'actions/intercomActions'
import { setRedirectedAction, setAddOnUpgradeDismissed } from 'actions/planActions'
import AIChatUpgradeModal from '../../sharedComponents/AIChatBox/AIChatUpgradeModal'

const markdownComponents = {
  p: ({ node, ...props }) => <div className="tw-last:mb-0" {...props} />,
  ul: ({ node, ...props }) => <ul className="tw-my-1 tw-pl-4 tw-last:mb-0" {...props} />,
  h4: ({ node, ...props }) => <h4 className="tw-mt-2 tw-mb-1 tw-text-sm tw-font-semibold tw-first:mt-0" {...props} />,
  h5: ({ node, ...props }) => <h5 className="tw-mt-2 tw-mb-1 tw-text-sm tw-font-medium tw-first:mt-0" {...props} />,
  table: ({ node, ...props }) => (
    <div className="tw-w-full tw-overflow-x-auto tw-mt-2 tw-mb-2">
      <table className="tw-min-w-full tw-text-sm tw-border-collapse tw-border tw-border-slate-300" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="tw-bg-slate-100" {...props} />,
  th: ({ node, ...props }) => (
    <th className="tw-px-3 tw-py-2 tw-text-left tw-font-semibold tw-border tw-border-slate-300 tw-text-xs sm:tw-text-sm tw-whitespace-nowrap" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="tw-px-3 tw-py-2 tw-border tw-border-slate-300 tw-text-xs sm:tw-text-sm tw-break-words" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a className="tw-text-blue-500 hover:tw-underline" {...props} target="_blank" rel="noopener noreferrer" />
  ),
}

// Pass configuration to remarkMath
const remarkMathOptions = {
  singleDollarTextMath: false, // This disables single dollar sign parsing
}

/** Stage CSV/XLSX via POST /chat/agent/attachments (MCP resolves by attachment_id). */
async function uploadSpreadsheetsAsAgentAttachments(items: { text?: string; file?: File; filename: string }[]) {
  type Item = {
    attachment_id: string
    filename: string | null
    size_bytes: number
    mime_type: string | null
    metadata: Record<string, unknown>
  }
  return Promise.all(
    items.map(async (item): Promise<Item> => {
      const fd = new FormData()
      if (item.file) {
        fd.append('file', item.file, item.filename || 'attachment.xlsx')
      } else {
        const lowerName = (item.filename || '').toLowerCase()
        const mime = lowerName.endsWith('.json') ? 'application/json' : lowerName.endsWith('.txt') ? 'text/plain' : 'text/csv'
        const blob = new Blob([item.text || ''], { type: `${mime};charset=utf-8` })
        fd.append('file', blob, item.filename || 'attachment.csv')
      }
      const res = await axios.post(`${openAIUrl}/chat/agent/attachments`, fd, getAxiosDefaultConfig())
      const d = res.data
      if (!d?.attachment_id) throw new Error('Staging spreadsheet failed: missing attachment_id')
      return {
        attachment_id: String(d.attachment_id),
        filename: d.filename ?? null,
        size_bytes: typeof d.size_bytes === 'number' ? d.size_bytes : 0,
        mime_type: typeof d.mime_type === 'string' ? d.mime_type : null,
        metadata: d.metadata && typeof d.metadata === 'object' ? d.metadata : {},
      }
    }),
  )
}

/** Badge text for an attached file, taken from its extension (CSV, XLSX, JSON, TXT, ...). */
const attachmentTypeLabel = (filename?: string | null): string => {
  const ext = (filename || '').split('.').pop()?.trim()
  return ext && ext.length <= 5 && ext !== filename ? ext.toUpperCase() : 'FILE'
}

type StagedAgentAttachment = Awaited<ReturnType<typeof uploadSpreadsheetsAsAgentAttachments>>[number]

const questionSuggestions = [
  'Write an executive summary',
  'How is this month going so far?',
  'Who are my top buyers last month?',
  'How is the business doing?',
  'Compare this year vs. last year so far',
]

const normalizeToolName = (name: string | undefined): string => {
  if (!name) return ''
  const n = name.toLowerCase()
  return n.startsWith('mcp__payhelm__') ? n.replace('mcp__payhelm__', '') : n
}

/** Any completed tool call can be saved as a scheduled task (no whitelist). */
const hasSchedulableTools = (steps: Array<{ tool?: string; status?: string }>): boolean => {
  if (!steps?.length) return false
  return steps.some(s => s.status === 'completed' && !!s.tool)
}

const getSchedulableStep = (steps: Array<{ tool?: string; toolInput?: any; toolOutput?: any; status?: string }>) => {
  if (!steps?.length) return null
  for (let i = steps.length - 1; i >= 0; i--) {
    const s = steps[i]
    if (s.status === 'completed' && s.tool) {
      return { toolName: normalizeToolName(s.tool), toolInput: s.toolInput || {}, step: s }
    }
  }
  return null
}

/**
 * Determine if a tool is a data-fetching tool (vs. a modification/action tool)
 * Data-fetching tools return data that should be displayed in the Data Explorer panel
 * Modification tools change settings but don't return displayable data
 */
const isDataFetchingTool = (toolName: string | undefined): boolean => {
  if (!toolName) return false
  // Strip SDK prefix (mcp__payhelm__) if present
  let name = toolName.toLowerCase()
  if (name.startsWith('mcp__payhelm__')) {
    name = name.replace('mcp__payhelm__', '')
  }

  // Explicitly excluded modification/action tools
  const modifyPrefixes = ['modify_', 'update_', 'set_', 'enable_', 'disable_', 'configure_', 'create_', 'delete_']
  const modifyTools = ['insure_shipment', 'void_insurance', 'export_to_email', 'export_to_google_drive', 'export_to_csv']

  if (modifyPrefixes.some(prefix => name.startsWith(prefix))) return false
  if (modifyTools.includes(name)) return false

  // Configuration/account tools - return metadata, not chartable analytics
  // These should NOT trigger the Data Explorer panel
  const configurationTools = [
    'get_google_ads_accounts',
    'list_google_ads_client_accounts',
    'get_facebook_ads_accounts',
    'get_openai_ads_accounts',
    'get_tiktok_accounts',
    'get_pinterest_accounts',
    'get_klaviyo_accounts',
    'get_bing_ads_accounts',
    'get_account_settings',
    'get_tracking_settings',
    'get_conversion_settings',
    'get_user_settings',
    'get_integration_status',
    'get_connected_accounts',
    'get_account_info',
    'get_account_details',
    'get_report_filter_options'
  ]

  if (configurationTools.includes(name)) return false

  // Data-fetching tools - search, get, fetch, query operations that return analytics data
  // Use includes() so platform-prefixed tools (ebay_get_, wix_get_, etc.) are also detected
  const fetchPatterns = ['search', 'get_', 'fetch', 'query']
  const fetchTools = ['run_mmm_simulation']

  if (fetchPatterns.some(pattern => name.includes(pattern))) return true
  if (fetchTools.includes(name)) return true

  // Default: treat unknown tools as non-data-fetching to be conservative
  return false
}

/**
 * Check if tool output contains chartable analytics data (not just configuration metadata)
 * Returns true if the data has multiple records OR contains numeric analytics fields
 */
const hasChartableData = (toolOutput: any): boolean => {
  if (!toolOutput) return false

  let data = toolOutput

  // Handle string outputs (try to parse JSON)
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return false
    }
  }

  // Extract rows from various result formats
  let rows: any[] = []
  if (Array.isArray(data)) {
    rows = data
  } else if (data?.results && Array.isArray(data.results)) {
    rows = data.results
  } else if (data?.data && Array.isArray(data.data)) {
    rows = data.data
  } else if (typeof data === 'object' && !Array.isArray(data)) {
    rows = [data]
  }

  // No data = not chartable
  if (rows.length === 0) return false

  // Multiple records (>3) suggests analytics data, not configuration
  if (rows.length > 3) return true

  // Check if any row has numeric analytics fields that would be meaningful to chart
  const analyticsFields = [
    'revenue', 'sales', 'orders', 'total', 'amount', 'quantity', 'count',
    'impressions', 'clicks', 'ctr', 'conversions', 'cost', 'spend', 'roas',
    'visitors', 'sessions', 'pageviews', 'bounce_rate', 'conversion_rate',
    'aov', 'ltv', 'cpc', 'cpm', 'cpa'
  ]

  const firstRow = rows[0]
  if (firstRow && typeof firstRow === 'object') {
    const keys = Object.keys(firstRow).map(k => k.toLowerCase())
    const hasAnalyticsField = analyticsFields.some(field =>
      keys.some(key => key.includes(field))
    )
    if (hasAnalyticsField) return true
  }

  // Small result set (1-3 records) without analytics fields = likely configuration data
  return false
}

/**
 * Clean markdown from text for voice output
 */
const cleanMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s*/g, '') // Remove headers
    .replace(/\*\*|__/g, '') // Remove bold
    .replace(/\*|_/g, '') // Remove italic
    .replace(/`{1,3}[^`]*`{1,3}/g, '') // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // Remove images
    .replace(/^[\s]*[-*+]\s+/gm, '') // Remove list markers
    .replace(/^[\s]*\d+\.\s+/gm, '') // Remove numbered list markers
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim()
}

// Placeholder for decimal points during sentence detection
const DECIMAL_PLACEHOLDER = '\u2024' // One dot leader - visually similar but distinct

// Number of conversation turns (user + assistant pairs) to show / load at a time
const TURN_PAGE_SIZE = 30
type ConversationMessage = { role: 'user' | 'bot'; message: string }

const splitHistoryTurns = (raw: string): string[] => {
  return (raw ?? '').split('|||||').map((part) => part.trim()).filter(Boolean)
}

const turnsToConversationMessages = (turns: string[], startOffset = 0): ConversationMessage[] => {
  return turns.map((turn, index) => ({
    role: (startOffset + index) % 2 === 0 ? 'user' : 'bot',
    message: String(turn ?? ''),
  }))
}

/**
 * Protects decimal numbers from being split as sentence boundaries
 * e.g., "23.6 million" -> "23\u20246 million"
 */
const protectDecimals = (text: string): string => {
  return text.replace(/(\d)\.(\d)/g, `$1${DECIMAL_PLACEHOLDER}$2`)
}

/**
 * Restores decimal points after sentence detection
 */
const restoreDecimals = (text: string): string => {
  return text.replace(new RegExp(DECIMAL_PLACEHOLDER, 'g'), '.')
}

/**
 * Extract first complete sentence from streaming content
 * Returns null if no complete sentence found yet
 */
const extractFirstSentence = (content: string): string | null => {
  const cleaned = cleanMarkdown(content)
  // Protect decimal numbers before sentence detection
  const protected_ = protectDecimals(cleaned)
  const match = protected_.match(/^[^.!?]+[.!?]/)
  if (match) {
    // Restore decimal points and trim
    const sentence = restoreDecimals(match[0]).trim()
    // Only return if it's a reasonable length (not too short, not too long)
    if (sentence.length >= 15 && sentence.length <= 200) {
      return sentence
    }
  }
  return null
}

/**
 * Generate a brief spoken summary for voice output
 * Creates a one-sentence response or contextual acknowledgment
 */
const generateSpokenSummary = (userQuestion: string, aiResponse: string): string => {
  // Extract topic from user question for contextual intro
  const questionLower = userQuestion.toLowerCase()

  // Clean the AI response - remove markdown
  const cleanedResponse = cleanMarkdown(aiResponse)

  // Protect decimal numbers before sentence detection (e.g., "$23.6 million")
  const protectedResponse = protectDecimals(cleanedResponse)

  // Try to extract the first sentence from the response
  const firstSentenceMatch = protectedResponse.match(/^[^.!?]+[.!?]/)
  const firstSentence = firstSentenceMatch ? restoreDecimals(firstSentenceMatch[0]).trim() : null

  // If we got a good first sentence under 150 chars, use it
  if (firstSentence && firstSentence.length <= 150 && firstSentence.length >= 20) {
    return firstSentence
  }

  // Otherwise, generate a contextual intro based on the question type
  let topic = 'your request'

  if (questionLower.includes('sales') || questionLower.includes('revenue')) {
    topic = 'your sales data'
  } else if (questionLower.includes('order')) {
    topic = 'your orders'
  } else if (questionLower.includes('customer')) {
    topic = 'your customers'
  } else if (questionLower.includes('product')) {
    topic = 'your products'
  } else if (questionLower.includes('summary') || questionLower.includes('executive')) {
    topic = 'your business summary'
  } else if (questionLower.includes('compare') || questionLower.includes('vs')) {
    topic = 'the comparison'
  } else if (questionLower.includes('month') || questionLower.includes('today') || questionLower.includes('week') || questionLower.includes('year')) {
    topic = 'your performance data'
  } else if (questionLower.includes('marketing') || questionLower.includes('utm')) {
    topic = 'your marketing analytics'
  } else if (questionLower.includes('shipping') || questionLower.includes('insure')) {
    topic = 'your shipping request'
  }

  // If response is short enough, just use it
  if (cleanedResponse.length <= 200) {
    return cleanedResponse
  }

  // Generate contextual intro + truncated response
  return `OK, I found information about ${topic}. ${firstSentence || cleanedResponse.slice(0, 120) + '...'}`
}

function AI({
  accounts,
  balance,
  isOwner,
  addOnEnabled,
  userCredits,
  addOnUpgradeDismissed,
  addOnPrices,
  isAdmin,
  mimicStatus,
  setAddOnUpgradeDismissed,
  setDoneAction,
  setRedirectedAction,
  prevAddOnNames,
  googleDriveAccounts
}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isCustomToolsMainView = searchParams.get('view') === 'custom-tools'
  const isScheduledTasksView = searchParams.get('view') === 'scheduled-tasks'
  const isChatHistoryView = searchParams.get('view') === 'chat-history'
  const isToolAccessView = searchParams.get('view') === 'tool-access'
  const scheduledTaskIdFromUrl = searchParams.get('taskId')
  const selectedTaskId = useSelector(selectSelectedTaskId)
  const scheduledTasks = useSelector(selectScheduledTasks)

  const { width: windowWidth, height: windowHeight } = useWindowSize()
  const isMobile = windowWidth < COMPACT_BREAKPOINT
  const isPhone = windowWidth < MOBILE_BREAKPOINT

  const [taskConfigPanelOpen, setTaskConfigPanelOpen] = useState(false)
  const [taskConfigPanelContext, setTaskConfigPanelContext] = useState<{
    message?: any
    agentSteps?: any[]
    conversationHistory?: any[]
    toolName: string
    toolParameters: Record<string, any>
    editMode?: boolean
    initialTask?: any
  } | null>(null)

  /** When entering a task from the list, open the edit panel once per task id (not on every scheduledTasks refresh). */
  const autoOpenedEditPanelForTaskIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!selectedTaskId) {
      autoOpenedEditPanelForTaskIdRef.current = null
      // openStandaloneTaskConfigPanel clears selection (null) while opening create/inline mode; do not
      // close the panel in that case. Only tear down when the panel was opened for list-driven edit.
      const closeForDeselect =
        taskConfigPanelOpen &&
        taskConfigPanelContext &&
        taskConfigPanelContext.editMode === true
      if (!closeForDeselect) return
      setTaskConfigPanelOpen(false)
      setTaskConfigPanelContext(null)
      return
    }

    const tasks = Array.isArray(scheduledTasks) ? scheduledTasks : []
    const task = tasks.find((t: any) => String(t?.id) === String(selectedTaskId))
    if (!task) {
      return
    }

    if (autoOpenedEditPanelForTaskIdRef.current === String(selectedTaskId)) {
      return
    }

    autoOpenedEditPanelForTaskIdRef.current = String(selectedTaskId)
    if (isMobile) {
      setTaskConfigPanelOpen(false)
      setTaskConfigPanelContext(null)
      return
    }
    setTaskConfigPanelContext({
      editMode: true,
      initialTask: task,
      toolName: task.tool_name || '',
      toolParameters: task.tool_parameters || {},
    })
    setTaskConfigPanelOpen(true)
  }, [selectedTaskId, scheduledTasks, isMobile])

  const HEADER_HEIGHT = 52
  const FOOTER_HEIGHT = 76
  const CHAT_TOP_MENU_HEIGHT = 84
  const CHAT_INPUT_SECTION_HEIGHT = 64
  const CHAT_INPUT_HEIGHT = 44
  const CHAT_INPUT_HEIGHT_MESSAGES = 56 // Bigger input when there are messages
  const MESSAGES_HEIGHT =
    windowHeight - CHAT_TOP_MENU_HEIGHT - CHAT_INPUT_SECTION_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT

  const [mounted, setMounted] = useState(false)
  const [saveReportModalOpen, setSaveReportModalOpen] = useState(false)
  const [saveReportModalData, setSaveReportModalData] = useState(null)
  const [saveReportName, setSaveReportName] = useState('')
  const [savedCustomerReportId, setSavedCustomerReportId] = useState(null)
  const [showFollowUp, setShowFollowUp] = useState(false)
  const [followUpMessageId, setFollowUpMessageId] = useState(null)
  const [followUserMessageId, setFollowUserMessageId] = useState(null)
  const [currentChatId, setCurrentChatId] = useState(null) // Track current chat's message_id
  const [isStreaming, setIsStreaming] = useState(false) // Track if currently streaming
  const [streamingMessageId, setStreamingMessageId] = useState(null) // Track message ID of streaming chat
  const [scheduleReportModalReportId, setScheduleReportModalReportId] = useState(null)
  const cancelTokenRef = useRef(null) // CancelToken for canceling requests
  const agentAbortControllerRef = useRef<AbortController | null>(null) // AbortController for agent requests
  const [voiceResponseEnabled, setVoiceResponseEnabled] = useState(false) // Auto-play TTS only when voice input is used
  const [lastCompletedResponse, setLastCompletedResponse] = useState<string | null>(null) // Track last AI response for TTS
  const voiceTriggeredRef = useRef(false) // Track if voice has been triggered for current response

  /**
   *
   * Fetch user and team accounts so we can get the ownerId
   *
   */
  const user = useSelector(selectRootUser)
  const teamAccounts = useSelector(selectTeamAccounts)
  const teamAccountsStatus = useSelector(selectTeamAccountsStatus)
  const customerReportId = useSelector((state) => state.customReports.reportId)
  const openAIChatStatus = useSelector(selectOpenAIChatStatus)

  // Agent mode state
  const agentModeEnabled = useSelector(selectAgentModeEnabled)
  const effortLevel = useSelector(selectEffortLevel)
  // #935: cost breakdown for the in-flight agent turn on this page.
  // Same per-chat bucket the agent thunk streams into (`sourcePage: chatMessageId`).
  // A hardcoded 'ai' key never matches, so the live cost line never appeared.
  const liveCreditSummary = useSelector((state: any) =>
    selectAgentCreditSummary(state, currentChatId),
  )
  const agentCatalog = useSelector(selectAgentModelCatalog)
  const agentModel = useSelector(selectAgentModel)
  const imageModel = useSelector(selectImageModel)
  // Deep Dive (effort 6): Astra directs, Fable executes. Drives the special
  // gradient composer treatment (Ultracode-style) below.
  const isDeepDive = agentModeEnabled && isDeepDiveLevel(agentCatalog, effortLevel)
  // Animated gradient border via the double-background trick (padding-box white
  // over an oversized border-box gradient whose position drifts), plus a soft
  // multi-color glow. Overrides the wrapper's border-color and background-color
  // classes without restructuring the DOM. Gradient starts and ends on the same
  // color so the 300%-wide loop is seamless.
  const deepDiveComposerStyle: React.CSSProperties = {
    border: '2px solid transparent',
    background:
      'linear-gradient(#fff, #fff) padding-box, linear-gradient(110deg, #f59e0b, #ec4899, #8b5cf6, #3b82f6, #ec4899, #f59e0b) border-box',
    backgroundSize: '100% 100%, 300% 100%',
    animation: 'ph-deep-dive-border 7s linear infinite',
    boxShadow: '0 4px 24px rgba(217, 70, 239, 0.20), 0 2px 12px rgba(59, 130, 246, 0.14)',
  }
  // Rendered once at the root of the AI component so composers don't duplicate it.
  const deepDiveGlowKeyframes = (
    <style>{'@keyframes ph-deep-dive-border { from { background-position: 0 0, 0% 50%; } to { background-position: 0 0, 300% 50%; } }'}</style>
  )
  // Per-chat streaming bucket key (cross-pollination fix): each chat thread reads/writes
  // its OWN Redux bucket keyed by its message_id, so two chats streaming at once never mix.
  // 'ai' is only the transient bucket for a brand-new chat before its id exists.
  const streamKey = currentChatId || 'ai'
  const agentSteps = useSelector((state) => selectAgentSteps(state, streamKey))
  const agentStatus = useSelector((state) => selectAgentStatus(state, streamKey))
  const agentMemoryUpdate = useSelector((state) => selectAgentMemoryUpdate(state, streamKey))
  const agentNarrations = useSelector((state) => selectAgentNarrations(state, streamKey))
  const agentAvailableTools = useSelector((state) => selectAgentAvailableTools(state, streamKey))
  const insightResponse = useSelector((state) => selectInsightResponse(state, streamKey))
  const insightStatus = useSelector((state) => selectInsightStatus(state, streamKey))
  const knowledgeSources = useSelector((state) => selectKnowledgeSources(state, streamKey))
  const generatedPdfsLive = useSelector((state) => selectGeneratedPdfs(state, streamKey))
  const activeChatIds = useSelector(selectActiveChatIds)
  const taskSuggestions = useSelector(selectTaskSuggestions)
  // Agent is "working" when the insight thunk is actively in-flight.
  // Using insightStatus avoids false positives from orphaned running steps.
  const isAgentWorking = insightStatus === 'loading'
  /** Send vs Stop: one condition for every composer so Stop stays visible while streaming or agent is in flight (including when the textarea has draft text). */
  const showStopInsteadOfSend = isStreaming || isAgentWorking
  const [agentMessageId, setAgentMessageId] = useState<string | null>(null)
  // TEMP DIAGNOSTIC — remove once the no-spinner bug is confirmed fixed.
  useEffect(() => {
    console.log('[DIAG-STREAM] agentMessageId/insightStatus/agentStatus changed', {
      agentMessageId, insightStatus, agentStatus, t: Date.now(),
    })
  }, [agentMessageId, insightStatus, agentStatus])
  // #935: the credit summary rides in on `additional_content`, which lands at
  // the very end of the stream — racing the `setAgentMessageId(null)` reset in
  // the completion effect below. When the reset wins, the summary has no
  // message to attach to and the cost line only appears after a reload. Keep
  // the id so a late arrival still lands on the right message.
  const lastAgentMessageIdRef = useRef<string | null>(null)
  const suggestionRequestedForMessageRef = useRef<Set<string>>(new Set())
  const prevChatIdForSuggestionsRef = useRef<string | null | undefined>(undefined)
  const [resultsPanelOpen, setResultsPanelOpen] = useState(true) // Right panel for results
  const [resultsPanelIndex, setResultsPanelIndex] = useState(0) // Which result to show
  const [agentDataPanelOpen, setAgentDataPanelOpen] = useState(false) // Agent data panel for tool results - starts closed, opens when agent runs
  const [agentDataPanelExpanded, setAgentDataPanelExpanded] = useState(false) // Expanded state for agent data panel
  // Report preview opened from a "View report" card (saved report the agent created); only the date range is editable here
  const [reportPanel, setReportPanel] = useState<{ reportId: string; title?: string; reportType?: string } | null>(null)
  const openReportPanel = React.useCallback((card: AgentReportCardData) => {
    setTaskConfigPanelOpen(false)
    setTaskConfigPanelContext(null)
    setAgentDataPanelExpanded(false)
    setResultsPanelOpen(false)
    setReportPanel({ reportId: card.id, title: card.title, reportType: card.reportType })
  }, [])
  useEffect(() => {
    setReportPanel(null)
  }, [currentChatId])

  // Chat preferences for seamless conversation resumption
  const chatPreferencesStatus = useSelector(selectChatPreferencesStatus)
  const lastActiveChatIdFromRedux = useSelector(selectLastActiveChatId)
  const autoLoadEnabled = useSelector(selectAutoLoadEnabled)
  const interruptedSessions = useSelector(selectInterruptedSessions)
  const [showResumptionBanner, setShowResumptionBanner] = useState(false)
  const [pendingResumption, setPendingResumption] = useState<any>(null)
  const [autoLoadAttempted, setAutoLoadAttempted] = useState(false)

  // Within-conversation turn pagination (for very long conversations)
  const [hasEarlierMessages, setHasEarlierMessages] = useState(false)
  const [earlierMessagesLoading, setEarlierMessagesLoading] = useState(false)
  const [currentTurnOffset, setCurrentTurnOffset] = useState(0)
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)

  // Question queue - allows chaining follow-up questions while agent is running
  const MAX_QUEUED_QUESTIONS = 10
  const [questionQueue, setQuestionQueue] = useState<string[]>([])
  const isProcessingQueueRef = useRef(false) // Prevent double-processing

  // Image upload for agent mode (up to 5 images). Ref holds loaded images so submit always has latest (avoids race with async setState).
  const MAX_IMAGES = 5
  const [attachedImages, setAttachedImages] = useState<{ base64: string; mediaType: string; fileName: string; previewUrl: string }[]>([])
  const attachedImagesRef = useRef<{ base64: string; mediaType: string; fileName: string; previewUrl: string }[]>([])

  // CSV upload for agent mode (up to 3 files). Ref keeps send always in sync.
  const MAX_CSVS = 3
  const [attachedCsvs, setAttachedCsvs] = useState<{ text?: string; file?: File; filename: string }[]>([])
  const attachedCsvsRef = useRef<{ text?: string; file?: File; filename: string }[]>([])

  // Large pastes collapse into a "clip" chip instead of ballooning the textarea (#1072).
  // Kept as plain text, folded back into the message body on send — this only changes
  // how it's displayed while composing, not what gets sent.
  const PASTE_CLIP_CHAR_THRESHOLD = 800
  const PASTE_CLIP_LINE_THRESHOLD = 12
  type TextClip = { id: string; label: string; text: string; lineCount: number }
  const [textClips, setTextClips] = useState<TextClip[]>([])
  const textClipsRef = useRef<TextClip[]>([])
  /** Staged agent attachment refs keyed by chat ID — re-inject for a few follow-up turns. */
  const PENDING_ATTACHMENT_FOLLOWUP_TURNS = 2
  type PendingStagedAttachments = {
    attachments: StagedAgentAttachment[]
    followUpsLeft: number
  }
  const pendingAgentAttachmentsRef = useRef<Record<string, PendingStagedAttachments>>({})
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  // Dynamic question suggestions based on user's integrations
  const dynamicSuggestions = useSelector((state) => selectRelatedQuestions(state, 'agent'))
  const dynamicSuggestionsStatus = useSelector((state) => selectRelatedQuestionsStatus(state, 'agent'))
  const suggestionsToShow = dynamicSuggestions.length > 0 ? dynamicSuggestions : questionSuggestions

  // Resizable chat panel
  const CHAT_WIDTH_KEY = 'payhelm_chat_panel_width'
  const MIN_CHAT_WIDTH = 360
  const MAX_CHAT_WIDTH_PERCENT = 0.7
  const DEFAULT_CHAT_WIDTH_PERCENT = 0.4
  const [chatPanelWidth, setChatPanelWidth] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(CHAT_WIDTH_KEY)
      if (saved) {
        const parsed = parseInt(saved, 10)
        if (!isNaN(parsed) && parsed >= MIN_CHAT_WIDTH) {
          return parsed
        }
      }
    }
    return 0 // 0 means use default percentage
  })
  const [isResizing, setIsResizing] = useState(false)
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null)
  const chatPanelWidthRef = useRef(chatPanelWidth)
  const chatAreaRef = useRef<HTMLDivElement>(null)

  useEffect(() => { chatPanelWidthRef.current = chatPanelWidth }, [chatPanelWidth])

  // Handle resize — only depends on isResizing so listeners aren't torn down on every move
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = e.clientX - resizeRef.current.startX
      const newWidth = Math.max(MIN_CHAT_WIDTH, resizeRef.current.startWidth + delta)
      const maxWidth = window.innerWidth * MAX_CHAT_WIDTH_PERCENT
      const clamped = Math.min(newWidth, maxWidth)
      setChatPanelWidth(clamped)
      chatPanelWidthRef.current = clamped
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      resizeRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      if (chatPanelWidthRef.current > 0) {
        localStorage.setItem(CHAT_WIDTH_KEY, String(chatPanelWidthRef.current))
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault()
    const chatEl = chatAreaRef.current
    if (chatEl) {
      resizeRef.current = {
        startX: e.clientX,
        startWidth: chatEl.offsetWidth
      }
      setIsResizing(true)
    }
  }

  const resetChatWidth = () => {
    setChatPanelWidth(0)
    chatPanelWidthRef.current = 0
    localStorage.removeItem(CHAT_WIDTH_KEY)
  }

  const [agentMcpSetupPending, setAgentMcpSetupPending] = useState(false)

  const handleToggleAgentMode = async () => {
    if (agentModeEnabled) {
      dispatch(toggleAgentMode())
      return
    }
    if (agentMcpSetupPending) return
    setAgentMcpSetupPending(true)
    try {
      const res = await axios.get(`${openAIUrl}/chat/agent/mcp-key-status`, getAxiosDefaultConfig())
      const { hasMcpKey } = res.data || {}
      if (hasMcpKey) {
        dispatch(toggleAgentMode())
        return
      }
      const ensureRes = await axios.post(`${openAIUrl}/chat/agent/ensure-mcp-key`, {}, getAxiosDefaultConfig())
      const data = ensureRes.data || {}
      if (!data.ok) {
        toast.error(data.error || 'Could not create your MCP key for Agent mode.')
        return
      }
      dispatch(toggleAgentMode())
      if (data.created) {
        toast.success('Agent mode on — we created your MCP key. Manage it anytime under Integrations → PayHelm MCP AI.')
      }
    } catch (err: any) {
      console.error('[Agent] mcp-key-status / ensure-mcp-key error:', err)
      const msg = err?.response?.data?.error
      toast.error(typeof msg === 'string' ? msg : 'Could not enable Agent mode. Please try again.')
    } finally {
      setAgentMcpSetupPending(false)
    }
  }

  // Effort config dropdown
  const [effortMenuOpen, setEffortMenuOpen] = useState(false)
  const effortMenuRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!effortMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (effortMenuRef.current && !effortMenuRef.current.contains(e.target as Node)) {
        setEffortMenuOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEffortMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [effortMenuOpen])

  // NOTE: Agent-related useEffects that reference messages/setMessages are defined
  // after the messages state declaration below (around line 835)

  const isTeamOwner = checkIsTeamOwner(user)
  useEffect(() => {
    if (teamAccountsStatus === 'idle') {
      dispatch(fetchTeamAccounts())
    }
  }, [teamAccountsStatus])

  // Handle add on not enabled, disabled for now
  // useEffect(() => {
  //   if (!addOnEnabled && userCredits <= 0) {
  //     console.log('AI add on not enabled')
  //     setDoneAction({ type: 'add_on_upgrade_modal_seen', addOn: 'AI' })
  //   }
  // }, [addOnEnabled, userCredits])

  // Cleanup streaming state on component unmount
  useEffect(() => {
    return () => {
      setIsStreaming(false)
      setStreamingMessageId(null)
      setLoadingMessage(null)
      // Clear cancel token reference on unmount
      cancelTokenRef.current = null
    }
  }, [])

  const ownerId = resolveIntegrationsOwnerId(user, teamAccounts)
  const openAIKeyId = getSecretId(isTeamOwner ? user.id : user.ownerId, 'openai')
  /** PayHelm MCP key_id must match the logged-in user (same as ensure-mcp-key / integrations_keys), not the team owner. */
  const agentKeyId = getSecretId(user.id, 'payhelm_ai_mcp')

  useEffect(() => {
    if (user?.email && (isScheduledTasksView || selectedTaskId)) {
      dispatch(fetchScheduledTasks() as any)
    }
  }, [dispatch, user?.email, isScheduledTasksView, selectedTaskId])

  /** Open a specific scheduled task from email links: /ai?view=scheduled-tasks&taskId=… */
  useEffect(() => {
    if (!isScheduledTasksView || !scheduledTaskIdFromUrl) return
    dispatch(setSelectedTaskId(scheduledTaskIdFromUrl))
  }, [dispatch, isScheduledTasksView, scheduledTaskIdFromUrl])

  const initialScheduledRunFromUrl =
    isScheduledTasksView &&
    scheduledTaskIdFromUrl &&
    selectedTaskId &&
    String(scheduledTaskIdFromUrl) === String(selectedTaskId)
      ? searchParams.get('runId')
      : null

  /**
   *
   * Fetch dashboard configs and filters
   *
   */
  const dashboardConfig = useSelector(selectDashboardConfig)
  // const rangeFilters = useSelector(selectRangeFilters)
  // const keywordFilters = useSelector(selectKeywordFilters)
  // const dashboardFilters = useSelector(selectDashboardFilters)

  /**
   *
   * Form setup
   *
   */
  const formRef = useRef(null)
  const gptFieldName = `gpt`
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return
    el.style.height = 'auto'
    const maxHeight = 200
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    reset: resetForm,
    resetField,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm({
    defaultValues: {
      model: 'gpt-3.5-turbo-1106',
      [gptFieldName]: '',
      overview: false,
      products: false,
      marketing: false,
      geographics: false,
      customers: false,
      generateCustomReport: false,
    },
  })

  const { ref: registerRef, ...registerRest } = register(gptFieldName, {})

  // PERF: do NOT subscribe to the draft with watch() — that re-rendered this whole
  // (very large) component on every keystroke, which is the "slow to type in long
  // chats" cause. The textarea stays uncontrolled (RHF tracks its value internally via
  // registerRest.onChange, with no subscriber there is no React re-render) and autoresize
  // runs as a DOM-only side effect in the textarea's onInput handler. The Send button no
  // longer reacts to draft emptiness (it's enabled unless loading); onSubmit guards empty.

  // Set the composer value programmatically (suggestions, voice, queue, prefill) and
  // keep the textarea height in sync — replaces the old watch()-driven resize effect.
  const setComposerValue = (value: string, opts?: any) => {
    setValue(gptFieldName, value, opts)
    requestAnimationFrame(() => autoResizeTextarea(textareaRef.current))
  }

  /** Enter-to-send must match Send button rules: while agent/stream runs the composer is often empty (we reset after send), so Enter must not submit a blank message. */
  const trySubmitComposerOnEnter = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey) return
    e.preventDefault()
    if (isStreaming || isAgentWorking) return
    if (openAIChatStatus === 'loading') return
    const draft = getValues(gptFieldName)
    const trimmed = typeof draft === 'string' ? draft.trim() : ''
    const hasAttachments = attachedImages.length > 0 || attachedCsvs.length > 0 || textClips.length > 0
    if (!trimmed && !hasAttachments) return
    handleSubmit(onSubmit)(e)
  }

  const [loadingMessage, setLoadingMessage] = useState(null)

  const handleRemoveImage = (index: number) => {
    setAttachedImages((prev) => {
      const next = prev.filter((_, i) => i !== index)
      attachedImagesRef.current = next
      return next
    })
  }

  const handleRemoveCsv = (index: number) => {
    setAttachedCsvs((prev) => {
      const next = prev.filter((_, i) => i !== index)
      attachedCsvsRef.current = next
      return next
    })
  }

  const handleRemoveTextClip = (id: string) => {
    setTextClips((prev) => {
      const next = prev.filter((c) => c.id !== id)
      textClipsRef.current = next
      return next
    })
  }

  const clearTextClips = () => {
    setTextClips([])
    textClipsRef.current = []
  }

  /** Intercepts large pastes into the composer and turns them into a clip chip instead of inline text. */
  const handleComposerPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData?.getData('text')
    if (!pasted) return
    const lines = pasted.split('\n')
    if (pasted.length < PASTE_CLIP_CHAR_THRESHOLD && lines.length < PASTE_CLIP_LINE_THRESHOLD) return

    e.preventDefault()
    const firstLine = lines.find((l) => l.trim().length > 0) || 'Pasted text'
    const slug = firstLine.trim().slice(0, 40).replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    const label = `Pasted-${slug || 'text'}_${Date.now()}.txt`
    const clip: TextClip = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label,
      text: pasted,
      lineCount: lines.length,
    }
    const next = [...textClipsRef.current, clip]
    textClipsRef.current = next
    setTextClips(next)
  }

  /** Folds clipped pastes back into the plain-text message body before it's sent. */
  const composeMessageWithClips = (typed: string, clips: TextClip[]): string => {
    const trimmedTyped = typeof typed === 'string' ? typed.trim() : ''
    if (!clips.length) return trimmedTyped
    const clipBlocks = clips.map((c) => c.text).join('\n\n')
    return trimmedTyped ? `${trimmedTyped}\n\n${clipBlocks}` : clipBlocks
  }

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const allFiles = Array.from(e.target.files || [])
    e.target.value = ''
    if (!allFiles.length) return

    const isSpreadsheetFile = (f: File) => {
      const name = f.name.toLowerCase()
      return (
        name.endsWith('.csv')
        || name.endsWith('.xlsx')
        || name.endsWith('.xls')
        || name.endsWith('.json')
        || name.endsWith('.txt')
        || f.type === 'application/json'
        || f.type === 'text/plain'
        || f.type === 'text/csv'
        || f.type === 'application/csv'
        || f.type === 'application/vnd.ms-excel'
        || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    }

    const spreadsheetFiles = allFiles.filter(isSpreadsheetFile)
    const imageFiles = allFiles.filter((f) => !isSpreadsheetFile(f))

    if (imageFiles.length > 0) {
      const remaining = MAX_IMAGES - attachedImagesRef.current.length
      if (remaining <= 0) {
        toast.warning(`Maximum ${MAX_IMAGES} images allowed`)
      } else {
        const toProcess = imageFiles.slice(0, remaining)
        if (imageFiles.length > remaining) {
          toast.warning(`Only ${remaining} more image${remaining === 1 ? '' : 's'} allowed — added first ${toProcess.length}`)
        }
        const validImages = toProcess.filter((f) => {
          if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not a supported image type`); return false }
          if (f.size > 32 * 1024 * 1024) { toast.error(`${f.name}: must be under 32MB`); return false }
          return true
        })
        if (validImages.length > 0) {
          Promise.all(validImages.map((file) =>
            new Promise<{ base64: string; mediaType: string; fileName: string; previewUrl: string }>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => {
                const dataUrl = reader.result as string
                const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
                const mediaType = (file.type || 'image/png') as 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
                resolve({ base64, mediaType, fileName: file.name, previewUrl: dataUrl })
              }
              reader.onerror = () => reject(reader.error)
              reader.readAsDataURL(file)
            })
          )).then((loaded) => {
            const next = [...attachedImagesRef.current, ...loaded].slice(0, MAX_IMAGES)
            attachedImagesRef.current = next
            setAttachedImages(next)
          }).catch((err) => {
            console.error('[AI] Image load failed:', err)
            toast.error('Failed to load one or more images')
          })
        }
      }
    }

    if (spreadsheetFiles.length > 0) {
      const remaining = MAX_CSVS - attachedCsvsRef.current.length
      if (remaining <= 0) {
        toast.warning(`Maximum ${MAX_CSVS} spreadsheet files allowed`)
      } else {
        const toProcess = spreadsheetFiles.slice(0, remaining)
        if (spreadsheetFiles.length > remaining) {
          toast.warning(`Only ${remaining} more spreadsheet file${remaining === 1 ? '' : 's'} allowed — added first ${toProcess.length}`)
        }
        const validFiles = toProcess.filter((f) => {
          if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name}: must be under 50 MB`); return false }
          return true
        })
        if (validFiles.length > 0) {
          Promise.all(validFiles.map((file) => {
            const name = file.name.toLowerCase()
            const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls')
            if (isExcel) {
              return Promise.resolve({ file, filename: file.name })
            }
            return new Promise<{ text: string; filename: string }>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve({ text: reader.result as string, filename: file.name })
              reader.onerror = () => reject(reader.error)
              reader.readAsText(file)
            })
          })).then((loaded) => {
            const next = [...attachedCsvsRef.current, ...loaded].slice(0, MAX_CSVS)
            attachedCsvsRef.current = next
            setAttachedCsvs(next)
          }).catch((err) => {
            console.error('[AI] Spreadsheet load failed:', err)
            toast.error('Failed to load one or more spreadsheet files')
          })
        }
      }
    }
  }

  // Reusable attachment preview strip — renders inside the input bubble (Claude-style).
  // The strip has a light bg + bottom divider so it's clearly a top section of the same
  // bordered container. Returns null when empty so the bubble keeps its normal height.
  const renderAttachmentStrip = (variant: 'inline' | 'compact' = 'inline') => {
    const hasAgentAttachments = agentModeEnabled && (attachedImages.length > 0 || attachedCsvs.length > 0)
    if (!hasAgentAttachments && textClips.length === 0) return null
    const imgSize = variant === 'compact' ? 'tw-w-10 tw-h-10' : 'tw-w-12 tw-h-12'
    return (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-rounded-t-lg">
        {textClips.map((clip) => (
          <PasteClipChip
            key={clip.id}
            label={clip.label}
            text={clip.text}
            lineCount={clip.lineCount}
            onRemove={() => handleRemoveTextClip(clip.id)}
          />
        ))}
        {agentModeEnabled && attachedImages.map((img, idx) => (
          <div key={`img-${idx}`} className="tw-relative tw-inline-block tw-flex-shrink-0">
            <img src={img.previewUrl} alt={img.fileName} className={`${imgSize} tw-object-cover tw-rounded-lg tw-border tw-border-gray-200`} />
            <button
              type="button"
              onClick={() => handleRemoveImage(idx)}
              className="tw-absolute tw--top-1.5 tw--right-1.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-gray-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-cursor-pointer tw-border-0 hover:tw-bg-red-500 tw-transition-colors"
            >
              &times;
            </button>
          </div>
        ))}
        {agentModeEnabled && attachedCsvs.map((csv, idx) => (
          <div key={`csv-${idx}`} className="tw-relative tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-pl-2 tw-pr-7 tw-py-1.5 tw-text-xs tw-text-gray-800 tw-max-w-[180px] tw-shadow-sm">
            <div className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-bg-green-100 tw-text-green-700 tw-flex-shrink-0">
              <FileTypeIcon filename={csv.filename} className="tw-w-3.5 tw-h-3.5" />
            </div>
            <div className="tw-flex tw-flex-col tw-min-w-0">
              <span className="tw-truncate tw-font-medium tw-text-gray-700">{csv.filename}</span>
              <span className="tw-text-[9px] tw-text-gray-400 tw-uppercase tw-tracking-wide">{attachmentTypeLabel(csv.filename)}</span>
            </div>
            <button
              type="button"
              onClick={() => handleRemoveCsv(idx)}
              className="tw-absolute tw-top-0.5 tw-right-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-gray-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-cursor-pointer tw-border-0 hover:tw-bg-red-500 tw-transition-colors"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    )
  }

  const onSubmit = async (values) => {
    const textClipsToSend = textClipsRef.current.length > 0 ? textClipsRef.current : textClips
    const messageContent = composeMessageWithClips(values[gptFieldName], textClipsToSend)
    const normalizedMessageContent = typeof messageContent === 'string' ? messageContent.trim() : ''
    const model = values.model
    const hasImageAttachments = attachedImagesRef.current.length > 0 || attachedImages.length > 0
    const hasCsvAttachments = attachedCsvsRef.current.length > 0 || attachedCsvs.length > 0
    const hasAnyAttachment = hasImageAttachments || hasCsvAttachments

    // While streaming or the agent thunk is in flight, ignore submits (prevents empty-message nudge from a stale Send click).
    if (isStreaming || isAgentWorking) {
      return
    }

    // Guard: block empty text submissions unless files are attached.
    if ((!normalizedMessageContent || normalizedMessageContent.toLowerCase() === 'undefined') && !hasAnyAttachment) {
      resetField(gptFieldName)
      toast.warning('Please enter a message before sending.')
      return
    }

    // If agent mode is enabled, use the agent handler
    if (agentModeEnabled) {
      // Effort is depth, not permission — no cap for pre-built tasks. The universal
      // "confirm before any change" rule (backend) already gates every mutation.
      const requestedEffortLevel = effortLevel
      // If agent is already running, add to queue — it will appear inline in the chat
      if (isAgentWorking) {
        if (questionQueue.length >= MAX_QUEUED_QUESTIONS) {
          toast.warning(`Maximum ${MAX_QUEUED_QUESTIONS} questions can be queued`)
          return
        }
        setQuestionQueue(prev => [...prev, normalizedMessageContent])
        resetField(gptFieldName)
        if (textClipsToSend.length > 0) clearTextClips()
        // Scroll to show the newly queued message
        setTimeout(() => scrollToBottom(), 100)
        return
      }

      // Only clear agent steps + panel for NEW conversations, not follow-ups
      // Follow-ups should preserve the Data Explorer with previously gathered results
      if (!currentChatId) {
        dispatch(clearAgentSteps({ sourcePage: 'ai' }))
        setAgentDataPanelOpen(false)
        setAgentDataPanelExpanded(false)
        prevHasDataRef.current = false // Reset tracking for auto-open
      }

      resetField(gptFieldName)

      // Use existing chat ID for follow-up questions, or create new one for first question
      let chatMessageId = currentChatId
      if (!chatMessageId) {
        chatMessageId = randomString(10)
        setCurrentChatId(chatMessageId)
        console.log(`[AI Chat] NEW CHAT: Created new chat ID: ${chatMessageId}`)
        // Surface the brand-new chat in the history list right away so its loading
        // indicator is visible (and the user can switch back to it while it runs).
        dispatch(upsertChatHistoryItem({
          message_id: chatMessageId,
          app_email: user.email,
          firstUserMessage: normalizedMessageContent,
          messages: [{ role: 'user', message: normalizedMessageContent }],
          created_at: new Date().toISOString(),
          is_private: false,
        }))
      } else {
        console.log(`[AI Chat] FOLLOW-UP: Using existing chat ID: ${chatMessageId}`)
      }

      // Flag this conversation as loading so the history list shows a spinner.
      // Cleared when the agent run settles below (success / error / stop), even
      // if the user has since switched to a different chat.
      dispatch(markChatLoading(chatMessageId))

      const userMessageId = uuidv4()
      const newAgentMessageId = uuidv4()
      setAgentMessageId(newAgentMessageId) // Track this message for Redux updates
      // askAgentForInsight's thunk body also dispatches this (line ~1011 in
      // openaiSlice.tsx), but only once dispatch(askAgentForInsight(...)) is
      // actually called below — which happens AFTER the awaited CSV-attachment
      // upload. Without this early dispatch, agentSteps/agentStatus for this
      // chat-scoped sourcePage stay on the PREVIOUS turn's values for the
      // entire upload duration, while agentMessageId already points at the
      // new placeholder — so the new bubble briefly renders the old turn's
      // finished chain-of-thought/answer before self-correcting once the
      // upload resolves and the real dispatch clears it. Dispatching here
      // too closes that window regardless of how long the upload takes.
      dispatch(clearAgentSteps({ sourcePage: chatMessageId }))

      // Capture image data from ref (so we have all loaded images even if state hasn't updated yet), then clear
      const imagesToSend = attachedImagesRef.current.length > 0 ? attachedImagesRef.current : attachedImages
      const imageForMessage = imagesToSend.length > 0 ? imagesToSend.map((img) => ({ previewUrl: img.previewUrl })) : undefined
      const imageForAgent =
        imagesToSend.length > 0
          ? imagesToSend
              .filter((img) => img.base64 && img.base64.length > 0)
              .map((img) => ({
                base64: img.base64,
                mediaType: img.mediaType || 'image/png',
              }))
          : undefined
      if (imagesToSend.length > 0 && (!imageForAgent || imageForAgent.length === 0)) {
        console.warn('[AI Chat] Attached images had no valid base64 — check handleAttachmentSelect')
      }

      // Guard: /chat/agent has a 5MB request-body limit. A too-large image (e.g. a
      // full-page screenshot) gets rejected by the server mid-upload and leaves the
      // UI spinning forever, so stop here with a clear message instead of sending.
      const MAX_AGENT_IMAGE_PAYLOAD = 4.5 * 1024 * 1024 // base64 chars ~= bytes on the wire; headroom under the 5MB body cap
      const totalImageBytes = (imageForAgent || []).reduce((sum, img) => sum + (img.base64?.length || 0), 0)
      if (totalImageBytes > MAX_AGENT_IMAGE_PAYLOAD) {
        toast.error(`Attached image is too large (~${Math.ceil(totalImageBytes / (1024 * 1024))}MB). Please use a smaller or cropped screenshot (under ~4MB) and try again.`)
        // No request goes out, so release the sidebar spinner claimed above.
        dispatch(markChatIdle(chatMessageId))
        setAgentMessageId(null)
        return
      }

      // Capture CSV data from ref, then clear
      const csvsToSend = attachedCsvsRef.current.length > 0 ? attachedCsvsRef.current : attachedCsvs
      const csvForAgent = csvsToSend.length > 0 ? csvsToSend : undefined

      setMessages([
        ...messages,
        { id: userMessageId, author: { email: user.email }, content: normalizedMessageContent, typedContent: typeof values[gptFieldName] === 'string' ? values[gptFieldName].trim() : '', clips: textClipsToSend.length > 0 ? textClipsToSend : undefined, images: imageForMessage, csvAttachments: csvsToSend.length > 0 ? csvsToSend.map(c => ({ filename: c.filename })) : undefined },
        {
          id: newAgentMessageId,
          author: { picture: ChatGPTLogo, email: null },
          content: AGENT_WORKING_PLACEHOLDER,
          isAgent: true,
        },
      ])
      setAttachedImages([])
      attachedImagesRef.current = []
      setAttachedCsvs([])
      attachedCsvsRef.current = []
      if (textClipsToSend.length > 0) clearTextClips()

      try {
        // Pass conversation history for context (previous messages, excluding the ones we just added)
        // This allows the agent to understand follow-up questions like "yes" or "tell me more"
        const conversationHistory = messages.filter(msg => msg.content) // Filter out empty messages
        console.log(`[AI Chat] Passing ${conversationHistory.length} messages as conversation history, chatId: ${chatMessageId}`)

        let agentAttachmentsPayload: Awaited<ReturnType<typeof uploadSpreadsheetsAsAgentAttachments>> | undefined
        let csvAttachmentsForRequest = csvForAgent
        if (csvForAgent?.length) {
          try {
            agentAttachmentsPayload = await uploadSpreadsheetsAsAgentAttachments(csvForAgent)
            if (chatMessageId) {
              pendingAgentAttachmentsRef.current[chatMessageId] = {
                attachments: agentAttachmentsPayload,
                followUpsLeft: PENDING_ATTACHMENT_FOLLOWUP_TURNS,
              }
            }
            csvAttachmentsForRequest = undefined
          } catch (uploadErr: any) {
            console.error('[AI Chat] Agent CSV staging failed — falling back to inline csvAttachments', uploadErr)
            const apiErr = uploadErr?.response?.data?.error
            toast.warning(typeof apiErr === 'string' ? apiErr : 'Could not stage CSV on server; sending inline instead.')
            agentAttachmentsPayload = undefined
            if (chatMessageId) {
              delete pendingAgentAttachmentsRef.current[chatMessageId]
            }
            csvAttachmentsForRequest = csvForAgent
          }
        } else if (chatMessageId) {
          const pendingForChat = pendingAgentAttachmentsRef.current[chatMessageId]
          if (pendingForChat?.attachments?.length && pendingForChat.followUpsLeft > 0) {
            // Re-inject staged attachment metadata on follow-up turns (e.g. "continue").
            // Allow a few turns so a retry still has attachment_id(s) if the first
            // approval/classifier pass did not trigger the import.
            agentAttachmentsPayload = pendingForChat.attachments
            pendingForChat.followUpsLeft -= 1
            if (pendingForChat.followUpsLeft <= 0) {
              delete pendingAgentAttachmentsRef.current[chatMessageId]
            }
          }
        }

        // Create AbortController for this request
        const abortController = new AbortController()
        agentAbortControllerRef.current = abortController

        const agentResult = await dispatch(askAgentForInsight({
          key_id: agentKeyId,
          question: normalizedMessageContent,
          // Per-chat Redux bucket (cross-pollination fix): stream into this chat's own
          // bucket, not a shared 'ai' bucket, so concurrent chats never mix.
          sourcePage: chatMessageId,
          // Recent Chats filters on named sources (ai / dashboard bubbles). Do not
          // persist the Redux bucket id as source_page or the chat vanishes on refetch.
          historySourcePage: 'ai',
          isMimic: user?.is_mimic,
          messageId: chatMessageId, // Use same chat ID for all messages in this conversation
          conversationHistory, // Include previous messages for context
          signal: abortController.signal, // Pass signal for cancellation
          effortLevel: requestedEffortLevel,
          model: agentModel, // Model selection support
          imageModel, // AI image model for wordpress_generate_image
          images: imageForAgent, // Optional images for vision analysis
          ...(csvAttachmentsForRequest?.length ? { csvAttachments: csvAttachmentsForRequest } : {}),
          ...(agentAttachmentsPayload?.length ? { agentAttachments: agentAttachmentsPayload } : {}),
          // Forward Task Library task context (if any). Backend turns this into
          // a short system-prompt hint centered on the task name + objective.
          ...(activePreBuiltTaskContext ? { preBuiltTask: activePreBuiltTaskContext } : {}),
        }))

        // Out of AI credits (402): the thunk rejects without throwing, so surface the
        // actionable message + upgrade CTA on the agent bubble instead of a blank/stuck one.
        if (askAgentForInsight.rejected.match(agentResult) && (agentResult.payload as any)?.insufficientCredits) {
          const creditError = (agentResult.payload as any)?.error
          const creditMsg = insufficientAiCreditsMessage(creditError)
          setMessages((prev) => prev.map((msg) =>
            msg.id === newAgentMessageId
              ? { ...msg, content: creditMsg }
              : msg
          ))
        }

        // Clear the controller after successful completion
        agentAbortControllerRef.current = null
        // The task brief is one-shot: once the agent has been kicked off with it,
        // drop it so subsequent follow-up questions in the same chat don't keep
        // re-injecting the same system message.
        if (activePreBuiltTaskContext) {
          dispatch(clearActivePreBuiltTaskContext())
        }
        // Response is streamed via Redux state - handled by useEffect

        // Turn finished — clear the shared loading flag for this conversation.
        dispatch(markChatIdle(chatMessageId))

        // Refresh chat history so this conversation shows in the sidebar
        dispatch(fetchOpenAIChatHistory({ app_email: user.email }))
      } catch (error) {
        console.error('Agent error:', error)
        agentAbortControllerRef.current = null
        dispatch(markChatIdle(chatMessageId))
        // Don't show error for cancelled requests
        if (error?.cancelled) {
          return
        }
        setMessages((prev) => prev.map((msg) =>
          msg.id === newAgentMessageId
            ? { ...msg, content: `Agent error: ${error?.message || String(error) || 'Unknown error'}` }
            : msg
        ))
        setAgentMessageId(null)
      }
      return
    }

    // Regular chat mode
    setLoadingMessage('Analyzing...')
    const timeouts = []
    timeouts.push(setTimeout(() => setLoadingMessage('Thinking...'), 9000))
    timeouts.push(setTimeout(() => setLoadingMessage('This is taking a little longer than usual...'), 55000))
    resetField(gptFieldName)

    const userMessageId = uuidv4()
    const chatGPTId = uuidv4()
    const newMessages = [
      ...messages,
      { id: userMessageId, author: { email: user.email }, content: normalizedMessageContent },
      {
        id: chatGPTId,
        author: { picture: ChatGPTLogo, email: null },
        content: null,
        assistantReportConfig: [], // Initialize with empty array
      },
    ]
    setMessages(newMessages)
    if (textClipsToSend.length > 0) clearTextClips()

    let messageId = currentChatId
    if (!messageId) {
      messageId = randomString(10)
      setCurrentChatId(messageId)
      dispatch(upsertChatHistoryItem({
        message_id: messageId,
        app_email: user.email,
        firstUserMessage: normalizedMessageContent,
        messages: [{ role: 'user', message: normalizedMessageContent }],
        created_at: new Date().toISOString(),
        is_private: false,
      }))
    }

    // // Call OpenAI Chat API
    const isMimic = user?.is_mimic

    const openAIChatPayload = {
      isMimic,
      key_id: openAIKeyId,
      // admin_key_id: ADMIN_OPENAI_KEY_ID,
      userMessage: normalizedMessageContent,
      model,
      generateCustomReport: values.generateCustomReport || false,
      message_id: messageId, // Include the current chat's message_id for context
      customReportConfig: {
        startDate: { timeframe: 'months', decrement: 1 },
        interval: 'MONTHLY',
        customReport: {
          app_email: user.email,
          name: user.email,
          dashboard_config: dashboardConfig,
          filters: dashboardInitialState.filters,
          filter_configs: { rangeFilters: rangeFiltersInitialState, keywordFilters: keywordFiltersInitialState },
        },
      },
    }

    const filteredMessages = newMessages.filter((message) => message.id !== chatGPTId)
    const nextNewMessage = newMessages.find((message) => message.id === chatGPTId)

    // Create CancelToken for this request
    const cancelToken = axios.CancelToken.source()
    cancelTokenRef.current = cancelToken

    try {
      // Set streaming state
      setIsStreaming(true)
      setStreamingMessageId(messageId)
      dispatch(markChatLoading(messageId))
      voiceTriggeredRef.current = false // Reset voice triggered flag for new response

      const callbackOnUpdateMessage = ({ content, ...updatePayload }) => {
        setMessages([
          ...filteredMessages,
          {
            ...nextNewMessage,
            content: content,
            ...updatePayload,
          },
        ])

        // Trigger voice as soon as first sentence is available (while still streaming)
        if (voiceResponseEnabled && content && !voiceTriggeredRef.current && updatePayload.messageStatus) {
          const firstSentence = extractFirstSentence(content)
          if (firstSentence) {
            voiceTriggeredRef.current = true
            setLastCompletedResponse(firstSentence)
          }
        }

        // Check if streaming is complete (no messageStatus means done)
        if (!updatePayload.messageStatus) {
          setIsStreaming(false)
          setStreamingMessageId(null)
          dispatch(markChatIdle(messageId))
          cancelTokenRef.current = null

          // If voice hasn't been triggered yet (short response), trigger now with full summary
          if (voiceResponseEnabled && content && !voiceTriggeredRef.current) {
            const spokenResponse = generateSpokenSummary(messageContent, content)
            setLastCompletedResponse(spokenResponse)
          }
        }
      }
      const callbackOnError = (error) => {
        console.log('[callbackOnError]', error)
        setMessages([...filteredMessages, { author: { picture: ChatGPTLogo, email: null }, content: error }])
        // Clear streaming state on error
        setIsStreaming(false)
        setStreamingMessageId(null)
        dispatch(markChatIdle(messageId))
        cancelTokenRef.current = null
      }
      await dispatch(
        callOpenAIChat({
          openAIChatPayload,
          callbackOnUpdateMessage,
          callbackOnError,
          setLoadingMessage,
          cancelToken,
        })
      )
    } catch (e) {
      // Check if it was a cancel error (user cancellation)
      if (axios.isCancel(e)) {
        setMessages([
          ...filteredMessages,
          {
            ...nextNewMessage,
            content: 'Request cancelled by user.',
          },
        ])
      } else {
        setMessages([
          ...filteredMessages,
          {
            author: { picture: ChatGPTLogo, email: null },
            content: 'Something went wrong. Please try again.',
            rating: undefined,
            ratings: {},
          },
        ])
      }
      timeouts.forEach(clearTimeout)
      setLoadingMessage('')
      // Clear streaming state on error
      setIsStreaming(false)
      setStreamingMessageId(null)
      dispatch(markChatIdle(messageId))
      cancelTokenRef.current = null
    }

    // Update the chat history view
    dispatch(fetchOpenAIChatHistory({ app_email: user.email }))
  }

  /**
   * Handle export button clicks - sends a message like a regular chat
   */
  const handleExportAction = async (exportType, reportId) => {
    // Create the export message based on the type
    const exportMessages = {
      email: `Export this report to email`,
      csv: `Export this report to CSV`,
      google_drive: `Export this report to Google Drive`,
    }

    const messageContent = exportMessages[exportType]

    // Reuse the onSubmit logic
    setLoadingMessage('Processing export...')
    const timeouts = []
    timeouts.push(setTimeout(() => setLoadingMessage('Preparing export...'), 9000))
    timeouts.push(setTimeout(() => setLoadingMessage('This is taking a little longer than usual...'), 55000))

    const userMessageId = uuidv4()
    const chatGPTId = uuidv4()
    const newMessages = [
      ...messages,
      { id: userMessageId, author: { email: user.email }, content: messageContent },
      {
        id: chatGPTId,
        author: { picture: ChatGPTLogo, email: null },
        content: null,
        assistantReportConfig: [], // Initialize with empty array
      },
    ]
    setMessages(newMessages)

    let messageId = currentChatId
    if (!messageId) {
      messageId = randomString(10)
      setCurrentChatId(messageId)
    }

    const isMimic = user?.is_mimic
    const model = watch('model')

    const openAIChatPayload = {
      isMimic,
      key_id: openAIKeyId,
      userMessage: messageContent,
      model,
      generateCustomReport: false,
      message_id: messageId,
      action: exportType, // Include export type in payload
      report_id: reportId, // Include report config for context
    }

    const filteredMessages = newMessages.filter((message) => message.id !== chatGPTId)
    const nextNewMessage = newMessages.find((message) => message.id === chatGPTId)

    const cancelToken = axios.CancelToken.source()
    cancelTokenRef.current = cancelToken

    // CSV accumulator for streaming
    let csvData = ''

    try {
      setIsStreaming(true)
      setStreamingMessageId(messageId)
      voiceTriggeredRef.current = false // Reset voice triggered flag for new response

      const callbackOnUpdateMessage = ({ content, csvChunk, progress, ...updatePayload }) => {
        // Accumulate CSV chunks
        if (exportType === 'csv' && csvChunk) {
          console.log('CSV Chunk', csvChunk)
          csvData += csvChunk
        }

        setMessages([
          ...filteredMessages,
          {
            ...nextNewMessage,
            content: content,
            ...updatePayload,
          },
        ])

        // Trigger voice as soon as first sentence is available (while still streaming)
        if (voiceResponseEnabled && content && !voiceTriggeredRef.current && updatePayload.messageStatus) {
          const firstSentence = extractFirstSentence(content)
          if (firstSentence) {
            voiceTriggeredRef.current = true
            setLastCompletedResponse(firstSentence)
          }
        }

        // When streaming is complete
        if (!updatePayload.messageStatus) {
          setIsStreaming(false)
          setStreamingMessageId(null)
          cancelTokenRef.current = null

          // If voice hasn't been triggered yet (short response), trigger now with full summary
          if (voiceResponseEnabled && content && !voiceTriggeredRef.current) {
            const spokenResponse = generateSpokenSummary(messageContent, content)
            setLastCompletedResponse(spokenResponse)
          }

          // Trigger CSV download if we have data
          if (exportType === 'csv' && csvData) {
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `AI_Report_${reportId}_${Date.now()}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            toast.success('CSV file downloaded successfully!')
          }
        }
      }
      const callbackOnError = (error) => {
        console.log('[callbackOnError]', error)
        setMessages([...filteredMessages, { author: { picture: ChatGPTLogo, email: null }, content: error }])
        setIsStreaming(false)
        setStreamingMessageId(null)
        cancelTokenRef.current = null
      }
      await dispatch(
        callOpenAIChat({
          openAIChatPayload,
          callbackOnUpdateMessage,
          callbackOnError,
          setLoadingMessage,
          cancelToken,
        })
      )
    } catch (e) {
      if (axios.isCancel(e)) {
        setMessages([
          ...filteredMessages,
          {
            ...nextNewMessage,
            content: 'Export cancelled by user.',
          },
        ])
      } else {
        setMessages([
          ...filteredMessages,
          {
            author: { picture: ChatGPTLogo, email: null },
            content: 'Something went wrong with the export. Please try again.',
            rating: undefined,
            ratings: {},
          },
        ])
      }
      timeouts.forEach(clearTimeout)
      setLoadingMessage('')
      setIsStreaming(false)
      setStreamingMessageId(null)
      cancelTokenRef.current = null
    }

    // Update the chat history view
    dispatch(fetchOpenAIChatHistory({ app_email: user.email }))
  }

  const setRating = async (messageId, rating, index) => {
    // Update the messages state with the new rating structure
    setMessages(
      messages.map((message) => {
        if (message.messageId === messageId) {
          return {
            ...message,
            ratings: {
              ...message.ratings,
              [index]: { rating: rating },
            },
          }
        }
        return message
      })
    )
    const { payload: openAIChatRes } = await dispatch(rateOpenAIChat({ message_id: messageId, rating: rating, index }))
  }


  /**
   *
   * Handle messages (including scrolling to bottom)
   *
   */
  const [messages, setMessages] = useState([])
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)

  const lastSchedulableForRail = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (!m?.isAgent || !m.agentSteps?.length) continue
      if (!hasSchedulableTools(m.agentSteps)) continue
      const sched = getSchedulableStep(m.agentSteps)
      if (!sched) continue
      return {
        message: m,
        agentSteps: m.agentSteps,
        toolName: sched.toolName,
        toolParameters: sched.toolInput || {},
      }
    }
    return null
  }, [messages])

  const openInlineTaskConfigPanel = React.useCallback(
    (ctx: {
      message?: any
      agentSteps?: any[]
      toolName: string
      toolParameters: Record<string, any>
      conversationHistory?: any[]
      editMode?: boolean
      initialTask?: any
    }) => {
      setResultsPanelOpen(false)
      setAgentDataPanelExpanded(false)
      setTaskConfigPanelContext({
        message: ctx.message,
        agentSteps: ctx.agentSteps,
        toolName: ctx.toolName,
        toolParameters: ctx.toolParameters,
        conversationHistory: ctx.conversationHistory ?? messages,
        editMode: ctx.editMode,
        initialTask: ctx.initialTask,
      })
      setTaskConfigPanelOpen(true)
    },
    [messages]
  )

  /** Create a scheduled task from scratch (no chat synthesis); use "Save as scheduled task" from the chat rail to pre-fill from a conversation. */
  const openStandaloneTaskConfigPanel = React.useCallback(() => {
    dispatch(setSelectedTaskId(null))
    dispatch(clearTaskIntentSynthesis() as any)
    setResultsPanelOpen(false)
    setAgentDataPanelExpanded(false)
    setTaskConfigPanelContext({
      toolName: '',
      toolParameters: {},
      conversationHistory: [],
      editMode: false,
    })
    setTaskConfigPanelOpen(true)
  }, [dispatch])

  const newScheduledTaskParam = searchParams.get('newScheduledTask')
  useEffect(() => {
    if (newScheduledTaskParam !== '1') return
    openStandaloneTaskConfigPanel()
    const next = new URLSearchParams(searchParams)
    next.delete('newScheduledTask')
    setSearchParams(next, { replace: true })
  }, [newScheduledTaskParam, openStandaloneTaskConfigPanel, searchParams, setSearchParams])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShowScrollToBottom(false)
  }

  const handleScrollToBottom = (e) => {
    e.preventDefault()
    e.stopPropagation()
    scrollToBottom()
    setShowScrollToBottom(false)
  }

  const [prevMessagesLength, setPrevMessagesLength] = useState(0)

  useEffect(() => {
    if (!mounted) {
      setMounted(true)
      return
    }
    if (messages.length > prevMessagesLength) scrollToBottom()
    setPrevMessagesLength(messages.length)
  }, [messages.length, mounted])

  // Auto-focus input fields
  useEffect(() => {
    // Focus the input field after a short delay to ensure it's rendered
    setTimeout(() => {
      setFocus(gptFieldName)
    }, 100)
  }, [messages.length, setFocus])

  // Handle scroll to detect if user is at bottom
  useEffect(() => {
    const handleScroll = () => {
      if (!messagesContainerRef.current) return

      const container = messagesContainerRef.current
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100

      // Only show scroll button if there are messages and user is not at bottom
      setShowScrollToBottom(messages.length > 0 && !isAtBottom)
    }

    const container = messagesContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      // Check initial state whenever messages change
      setTimeout(() => handleScroll(), 50)

      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [messages]) // Changed from messages.length to messages to trigger on content changes

  // Clear proactive task suggestions when switching chats
  useEffect(() => {
    if (prevChatIdForSuggestionsRef.current !== currentChatId) {
      prevChatIdForSuggestionsRef.current = currentChatId
      dispatch(clearTaskSuggestions())
      suggestionRequestedForMessageRef.current = new Set()
    }
  }, [currentChatId, dispatch])

  // Agent mode useEffects - must be after messages state is declared

  // Update agent message when insight response changes
  useEffect(() => {
    if (agentMessageId && insightResponse) {
      setMessages((prev) => prev.map((msg) =>
        msg.id === agentMessageId
          ? { ...msg, content: insightResponse }
          : msg
      ))
    }
  }, [insightResponse, agentMessageId])

  // Update agent message with steps when status changes or steps update
  // C-suite reviews are now embedded as agent steps, so no separate save needed
  useEffect(() => {
    if (agentMessageId) lastAgentMessageIdRef.current = agentMessageId
    if (agentMessageId && agentSteps.length > 0) {
      setMessages((prev) => prev.map((msg) =>
        msg.id === agentMessageId
          ? { ...msg, agentSteps: [...agentSteps], agentStatus }
          : msg
      ))
    }
  }, [agentSteps, agentStatus, agentMessageId])

  // #935: a summary arriving after a chat switch must not attach to the
  // previous conversation's last agent message.
  useEffect(() => {
    lastAgentMessageIdRef.current = null
  }, [currentChatId])

  // #935: attach the cost breakdown whenever it arrives, independently of the
  // run still being active. Deliberately not guarded on `agentMessageId`: that
  // is nulled the moment the run completes, which is the same tick the summary
  // arrives on, so guarding on it loses the race roughly every time.
  useEffect(() => {
    const targetId = agentMessageId ?? lastAgentMessageIdRef.current
    if (!targetId || !liveCreditSummary) return
    setMessages((prev) => prev.map((msg) =>
      msg.id === targetId ? { ...msg, creditSummary: liveCreditSummary } : msg
    ))
  }, [liveCreditSummary, agentMessageId])

  // Attach agent-generated PDFs to the live message so the download card shows
  // immediately (additional_content is otherwise dropped until reload).
  useEffect(() => {
    if (agentMessageId && generatedPdfsLive.length > 0) {
      setMessages((prev) => prev.map((msg) =>
        msg.id === agentMessageId
          ? { ...msg, generatedPdfs: generatedPdfsLive }
          : msg
      ))
    }
  }, [generatedPdfsLive, agentMessageId])

  // Proactive scheduled-task suggestion (backend Haiku classifier)
  useEffect(() => {
    if (!agentModeEnabled) return
    if (insightStatus !== 'succeeded') return
    if (!agentMessageId) return
    if (!hasSchedulableTools(agentSteps)) return
    if (suggestionRequestedForMessageRef.current.has(agentMessageId)) return

    const last = messages[messages.length - 1]
    if (!last?.isAgent || last.id !== agentMessageId) return

    suggestionRequestedForMessageRef.current.add(agentMessageId)

    const historyForSuggest = messages.map((msg) =>
      msg.id === agentMessageId
        ? { ...msg, agentSteps: [...agentSteps], agentStatus: 'completed' }
        : msg
    )

    dispatch(
      suggestScheduledTask({
        messageId: agentMessageId,
        conversationHistory: historyForSuggest,
        agentSteps: [...agentSteps],
      }) as any
    )
  }, [agentModeEnabled, insightStatus, agentMessageId, agentSteps, messages, dispatch])

  // Mark agent as complete when status changes.
  //
  // Gated on the TRANSITION into 'succeeded' (prev !== 'succeeded' && now ===
  // 'succeeded'), not merely "agentMessageId is set and insightStatus ===
  // 'succeeded'" — streamKey (and so insightStatus) is keyed per chat, not
  // per turn, so it stays 'succeeded' from the previous turn until
  // askAgentForInsight's pending case flips it to 'loading' for the new one.
  // Attaching a file re-uploads it (uploadSpreadsheetsAsAgentAttachments)
  // BEFORE that dispatch fires, so there's a real async window where a fresh
  // agentMessageId for the new turn coexists with the old turn's stale
  // 'succeeded' status. A level-check effect fires in that window (it's a
  // dependency of this effect, so a bare agentMessageId change re-runs it),
  // marking the brand-new turn "completed" with leftover insightResponse text
  // and nulling agentMessageId — which is why re-attaching a file mid-session
  // showed an instant "finished" answer with no spinner while the server kept
  // running. Edge-detecting the transition (matching the justCompleted
  // pattern in the effect right below this one) means a merely-still-stale
  // 'succeeded' status is a no-op.
  const prevInsightStatusForCompletionRef = useRef(insightStatus)
  useEffect(() => {
    const prevStatus = prevInsightStatusForCompletionRef.current
    prevInsightStatusForCompletionRef.current = insightStatus
    const justSucceeded = prevStatus !== 'succeeded' && insightStatus === 'succeeded'
    if (!justSucceeded || !agentMessageId) return
    setMessages((prev) => prev.map((msg) =>
      msg.id === agentMessageId
        ? {
            ...msg,
            // A run can finish without streaming any answer (tool-only turn,
            // Stop, time cap). Leaving the placeholder behind would keep the
            // bubble on a loader for good.
            content: resolveCompletedAgentContent({
              streamedResponse: insightResponse,
              currentContent: msg.content,
              hasSteps: hasSubstantiveAgentSteps(agentSteps),
            }),
            agentSteps: [...agentSteps],
            agentStatus: 'completed',
          }
        : msg
    ))
    setAgentMessageId(null) // Reset for next query
  }, [insightStatus, agentMessageId, agentSteps, insightResponse])

  // Auto-submit next queued question when agent completes
  const lastInsightStatusRef = useRef(insightStatus)
  useEffect(() => {
    // Detect transition from running to succeeded
    const justCompleted = lastInsightStatusRef.current !== 'succeeded' && insightStatus === 'succeeded'
    lastInsightStatusRef.current = insightStatus

    if (justCompleted && questionQueue.length > 0 && !isProcessingQueueRef.current) {
      isProcessingQueueRef.current = true

      // Small delay to allow UI to update and show the completed response
      const timeoutId = setTimeout(() => {
        const [nextQuestion, ...remainingQueue] = questionQueue
        setQuestionQueue(remainingQueue)

        // Set the input value
        setComposerValue(nextQuestion)

        // Use another small delay to ensure the form value is set, then click submit
        setTimeout(() => {
          const form = formRef.current
          if (form) {
            const submitBtn = form.querySelector('button[type="submit"]')
            if (submitBtn) {
              (submitBtn as HTMLButtonElement).click()
            }
          }
          isProcessingQueueRef.current = false
        }, 100)
      }, 500) // 500ms delay to let user see the response briefly

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [insightStatus, questionQueue, setValue])

  // Collapse sidebar when agent chat starts, but don't show data panel yet
  // Data panel will appear automatically when search/fetch tools are called
  // useEffect(() => {
  //   if (agentStatus === 'running') {
  //     // Collapse the sidebar to maximize workspace
  //     setSidebarCollapsed(true)
  //     // Keep panel hidden until tool results arrive - don't open prematurely
  //     // Panel visibility is controlled by the effect below
  //   }
  // }, [agentStatus])

  // Keep data panel visible (as side panel, collapsed) when data-fetching tools are called
  // Panel stays visible - user can expand/collapse but not hide it completely
  const prevHasDataRef = useRef(false)
  useEffect(() => {
    const hasDataFetchingToolCall = agentSteps.some(step =>
      step.tool && isDataFetchingTool(step.tool)
    )
    // When data tools are first called, show panel in collapsed state
    if (hasDataFetchingToolCall && !prevHasDataRef.current) {
      setAgentDataPanelOpen(true)
      setAgentDataPanelExpanded(false) // Always start collapsed
    }
    // Keep panel open if there's data
    if (hasDataFetchingToolCall) {
      setAgentDataPanelOpen(true)
    }
    prevHasDataRef.current = hasDataFetchingToolCall
  }, [agentSteps])

  // Auto-update results panel index when new results come in
  useEffect(() => {
    const resultsCount = messages
      .filter((msg) => !msg.author?.email || msg.author?.email !== user.email)
      .flatMap((msg) => (msg.assistantReportConfig || []).filter((c: any) => c?.reportData || c?.videoUrls?.length))
      .length
    if (resultsCount > 0 && resultsPanelIndex >= resultsCount) {
      setResultsPanelIndex(resultsCount - 1)
    }
    // Auto-show the latest result when new data comes in
    if (resultsCount > 0) {
      setResultsPanelIndex(resultsCount - 1)
      setResultsPanelOpen(true)
      setTaskConfigPanelOpen(false)
      setTaskConfigPanelContext(null)
    }
  }, [messages])

  /**
   *
   * Handle OpenAI Chat History
   *
   */
  // Chat history is owned by AppSidebar (fetch, load more, list). We only read for auto-load/resumption.
  const openAIChatHistoryStatus = useSelector(selectOpenAIChatHistoryStatus)
  const openAIChatHistory = useSelector(selectOpenAIChatHistory)

  const filteredChatHistory = _.chain(openAIChatHistory)
    .filter(({ app_email }) => app_email === user.email)
    .uniqBy('message_id')
    .value()

  // Fetch dynamic question suggestions when agent mode is enabled (once per mount)
  useEffect(() => {
    if (agentModeEnabled) {
      dispatch(fetchRelatedQuestions({ sourcePage: 'agent' }))
    }
  }, [agentModeEnabled])

  // Fetch chat preferences on mount (for seamless resumption)
  useEffect(() => {
    if (chatPreferencesStatus === 'idle' && user?.email) {
      dispatch(fetchChatPreferences())
    }
  }, [user, chatPreferencesStatus, dispatch])

  // Model/effort options come from papaya rather than a bundled list. Order
  // against fetchAgentSettings does not matter: whichever lands second runs
  // reconcileAgentSelection, so neither can discard the other's value.
  useEffect(() => {
    if (user?.email) {
      dispatch(fetchAgentModelCatalog())
    }
  }, [user?.email, dispatch])

  // Load persisted agent settings from DB (overrides localStorage once user is known)
  useEffect(() => {
    if (user?.email) {
      dispatch(fetchAgentSettings())
    }
  }, [user?.email, dispatch])

  // Sync currentChatId to localStorage and backend (debounced)
  useEffect(() => {
    if (currentChatId) {
      localStorage.setItem('payhelm_last_active_chat_id', currentChatId)

      // Debounce the backend update
      const timeoutId = setTimeout(() => {
        dispatch(updateChatPreferences({ lastActiveChatId: currentChatId }))
      }, 1000)

      return () => clearTimeout(timeoutId)
    }
  }, [currentChatId, dispatch])

  // Handle browser close/navigation - mark streaming session as interrupted
  useEffect(() => {
    const handleBeforeUnload = () => {
      const interruptMessageId = streamingMessageId || currentChatId
      if ((isStreaming || isAgentWorking) && interruptMessageId) {
        // Use sendBeacon for reliable delivery on page close
        const url = `/api/openai/chat/mark-interrupted/${interruptMessageId}`
        const data = JSON.stringify({ reason: 'browser_close' })
        navigator.sendBeacon(url, data)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isStreaming, isAgentWorking, streamingMessageId, currentChatId])

  /**
   * Build the UI messages array from role-tagged conversation objects.
   * `globalOffset` is the index of `items[0]` within the full conversation.
   */
  const buildConversationMessages = (
    items: ConversationMessage[],
    messageId: string,
    analytics: any,
    ratings: any,
    globalOffset: number = 0,
    /**
     * Whether this slice ends at the newest turn. The flat
     * `analytics.creditSummary` describes only the newest assistant turn, so it
     * must never be used as a fallback on an earlier page — `isLastAssistant`
     * below is last-bot-*in-this-slice*, which on "Load earlier messages" is a
     * middle-of-conversation turn.
     */
    isTailSlice: boolean = true,
  ) => {
    const conversationMessages: any[] = []
    const stepsByTurn = analytics?.agentStepsByTurn || {}
    // #935: cost per turn. The flat `analytics.creditSummary` is the *latest*
    // turn's figure, so using it for every message made older answers display
    // the newest cost. Fall back to it only for the last assistant turn, which
    // is the one it actually describes (and covers rows written before
    // creditSummaryByTurn existed).
    const creditSummaryByTurn = analytics?.creditSummaryByTurn || {}
    // Find the last assistant turn index — used as fallback when agentStepsByTurn is not available
    let lastAssistantIndex = -1
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i]?.role === 'bot') {
        lastAssistantIndex = i
        break
      }
    }
    for (let i = 0; i < items.length; i++) {
      const role = items[i]?.role
      const content = String(items[i]?.message || '').trim()
      const identifier = uuidv4()
      const globalIndex = globalOffset + i
      if (role === 'user') {
        if (!content) continue
        const attachmentsForTurn = analytics?.attachmentsByTurn?.[globalIndex]
        conversationMessages.push({
          id: identifier,
          author: { email: user.email },
          content,
          ratings: {},
          assistantReportConfig: [],
          // Reconstructed from `analytics.attachmentsByTurn` (persisted server-side
          // at turn-save time) so a reloaded chat still shows the sent-file chip —
          // see agentRunPersistence.ts's `userAttachments` handling.
          csvAttachments: Array.isArray(attachmentsForTurn) && attachmentsForTurn.length > 0
            ? attachmentsForTurn.map((a: { filename: string; kind?: string }) => ({ filename: a.filename, kind: a.kind }))
            : undefined,
        })
      } else {
        // Use per-turn steps if available, otherwise fall back to showing steps on last assistant only
        const turnSteps = stepsByTurn[globalIndex]
        const hasPerTurnSteps = Object.keys(stepsByTurn).length > 0
        const isLastAssistant = i === lastAssistantIndex
        const steps = hasPerTurnSteps
          ? (turnSteps || [])
          : (isLastAssistant ? (analytics?.agentSteps || []) : [])
        // An interrupted run can leave an answer-less assistant turn whose chain
        // of thought survived. Dropping it hid the whole turn and reloaded the
        // conversation blank, so keep it and say why the answer is missing.
        if (!shouldRenderRestoredAssistantTurn(content, steps.length)) continue
        conversationMessages.push({
          id: identifier,
          author: { picture: ChatGPTLogo, email: null },
          content: content || AGENT_NO_ANSWER_NOTICE,
          messageId,
          ratings: { ...(ratings || {}) },
          assistantReportConfig: analytics?.assistantReportConfig || [],
          // Per-turn when recorded; chats saved before that only have the
          // conversation-level list, which belongs on the last reply alone.
          generatedPdfs: analytics?.generatedPdfsByTurn
            ? (analytics.generatedPdfsByTurn[globalIndex] || [])
            : (isLastAssistant ? (analytics?.generatedPdfs || []) : []),
          isAgent: analytics?.agentMode || false,
          agentSteps: steps,
          agentStatus: toDisplayAgentStatus(analytics?.agentStatus, steps.length > 0),
          // #935: persisted per message, so the cost line survives a reload
          // rather than only existing on the live stream.
          creditSummary:
            creditSummaryByTurn[globalIndex]
            ?? (isTailSlice && isLastAssistant ? (analytics?.creditSummary || null) : null),
        })
      }
    }
    return conversationMessages
  }

  const handleChatHistoryClick = async (e, chat) => {
    e.preventDefault()

    setTaskConfigPanelOpen(false)
    setTaskConfigPanelContext(null)

    // Close mobile drawer when selecting a chat
    // setMobileDrawerOpen(false)

    const fallbackTurns = splitHistoryTurns(chat?.message || '')
    const fallbackOffset = fallbackTurns.length > TURN_PAGE_SIZE ? fallbackTurns.length - TURN_PAGE_SIZE : 0

    try {
      const result = await dispatch(
        fetchOpenAIChatConversation({
          app_email: user.email,
          message_id: chat.message_id,
          turnLimit: TURN_PAGE_SIZE,
        })
      ).unwrap()

      let conversationItems: ConversationMessage[] = []
      let resolvedOffset = fallbackOffset
      let hasEarlier = fallbackOffset > 0

      if (Array.isArray(result?.messages)) {
        conversationItems = result.messages
        resolvedOffset = typeof result.turnOffset === 'number' ? result.turnOffset : fallbackOffset
        hasEarlier = typeof result.hasEarlierTurns === 'boolean' ? result.hasEarlierTurns : fallbackOffset > 0
      } else if (Array.isArray(result?.turns)) {
        resolvedOffset = typeof result.turnOffset === 'number' ? result.turnOffset : fallbackOffset
        conversationItems = turnsToConversationMessages(result.turns, resolvedOffset)
        hasEarlier = typeof result.hasEarlierTurns === 'boolean' ? result.hasEarlierTurns : fallbackOffset > 0
      } else if (typeof result?.message === 'string') {
        const allTurns = splitHistoryTurns(result.message)
        resolvedOffset = allTurns.length > TURN_PAGE_SIZE ? allTurns.length - TURN_PAGE_SIZE : 0
        conversationItems = turnsToConversationMessages(allTurns.slice(resolvedOffset), resolvedOffset)
        hasEarlier = resolvedOffset > 0
      } else {
        conversationItems = turnsToConversationMessages(fallbackTurns.slice(fallbackOffset), fallbackOffset)
        resolvedOffset = fallbackOffset
        hasEarlier = fallbackOffset > 0
      }

      if (conversationItems.length < 1) {
        throw new Error('Conversation payload is empty')
      }

      const analytics = JSON.parse(result.analytics || chat.analytics || '{}')
      const conversationMessages = buildConversationMessages(
        conversationItems,
        chat.message_id,
        analytics,
        result.ratings || chat.ratings,
        resolvedOffset
      )

      setMessages(conversationMessages)
      setCurrentChatId(chat.message_id)
      setCurrentConversationId(chat.message_id)
      setHasEarlierMessages(hasEarlier)
      setCurrentTurnOffset(resolvedOffset)
      resetForm()
      setIsStreaming(false)
      setStreamingMessageId(null)
      setLoadingMessage(null)
      cancelTokenRef.current = null
      dispatch(clearAgentSteps({ sourcePage: 'ai' }))
      dispatch(resetInsightForSourcePage({ sourcePage: 'ai' }))
      setAgentMessageId(null)

      const hasAgentStepsInHistory = analytics.agentSteps?.length > 0 &&
        analytics.agentSteps.some((step: any) => step.tool && step.status === 'completed')
      setAgentDataPanelOpen(hasAgentStepsInHistory)
      setAgentDataPanelExpanded(false)
      prevHasDataRef.current = hasAgentStepsInHistory
      setTimeout(() => scrollToBottom(), 100)
    } catch (err) {
      console.error('[Chat] Failed to load conversation messages[]:', err)
    }
  }

  /** Load older messages (prepend) for a long conversation. */
  const handleLoadEarlierMessages = async () => {
    if (!currentConversationId || !user?.email || earlierMessagesLoading || currentTurnOffset <= 0) return
    setEarlierMessagesLoading(true)
    try {
      const newOffset = Math.max(0, currentTurnOffset - TURN_PAGE_SIZE)
      const limit = currentTurnOffset - newOffset
      const result = await dispatch(
        fetchOpenAIChatConversation({
          app_email: user.email,
          message_id: currentConversationId,
          turnLimit: limit,
          turnOffset: newOffset,
        })
      ).unwrap()

      let olderItems: ConversationMessage[] = []
      let resolvedOffset = newOffset
      let nextHasEarlier = newOffset > 0

      if (Array.isArray(result?.messages)) {
        olderItems = result.messages
        resolvedOffset = typeof result.turnOffset === 'number' ? result.turnOffset : newOffset
        nextHasEarlier = typeof result.hasEarlierTurns === 'boolean' ? result.hasEarlierTurns : newOffset > 0
      } else if (Array.isArray(result?.turns)) {
        resolvedOffset = typeof result.turnOffset === 'number' ? result.turnOffset : newOffset
        olderItems = turnsToConversationMessages(result.turns, resolvedOffset)
        nextHasEarlier = typeof result.hasEarlierTurns === 'boolean' ? result.hasEarlierTurns : newOffset > 0
      } else if (typeof result?.message === 'string') {
        const allTurns = splitHistoryTurns(result.message)
        olderItems = turnsToConversationMessages(allTurns.slice(newOffset, currentTurnOffset), newOffset)
      }

      if (olderItems.length === 0) {
        setHasEarlierMessages(false)
        return
      }
      const selectedChat = filteredChatHistory.find(c => c.message_id === currentConversationId)
      const analytics = JSON.parse(selectedChat?.analytics || '{}')
      // Not the tail slice: the flat creditSummary belongs to the newest turn,
      // which is not in this page.
      const olderMessages = buildConversationMessages(
        olderItems,
        currentConversationId,
        analytics,
        selectedChat?.ratings,
        resolvedOffset,
        false,
      )
      setMessages((prev) => [...olderMessages, ...prev])
      setCurrentTurnOffset(resolvedOffset)
      setHasEarlierMessages(nextHasEarlier)
    } catch (err) {
      console.error('[Chat] Failed to load earlier messages:', err)
      // Surface a non-blocking toast if the app has one; otherwise the button stays enabled for retry
    } finally {
      setEarlierMessagesLoading(false)
    }
  }

  // Auto-load last conversation when chat history is ready (must be after handleChatHistoryClick is defined).
  // Use primitive deps only so we don't re-run on every render (filteredChatHistory is a new array each time).
  useEffect(() => {
    if (
      !autoLoadAttempted ||
      !autoLoadEnabled ||
      openAIChatHistoryStatus !== 'succeeded' ||
      chatPreferencesStatus !== 'succeeded' ||
      messages.length !== 0 ||
      filteredChatHistory.length === 0
    ) {
      return
    }

    setAutoLoadAttempted(true)

    // Check for interrupted sessions first
    if (interruptedSessions.length > 0) {
      const mostRecentInterrupted = interruptedSessions[0]
      setPendingResumption(mostRecentInterrupted)
      setShowResumptionBanner(true)
      return
    }

    // Otherwise auto-load the last active chat
    const lastActiveChatId = lastActiveChatIdFromRedux || localStorage.getItem('payhelm_last_active_chat_id')
    const targetChat = lastActiveChatId
      ? filteredChatHistory.find(c => c.message_id === lastActiveChatId)
      : filteredChatHistory[0]

    if (targetChat) {
      handleChatHistoryClick({ preventDefault: () => {} }, targetChat)
    }
  }, [
    autoLoadAttempted,
    autoLoadEnabled,
    openAIChatHistoryStatus,
    chatPreferencesStatus,
    messages.length,
    filteredChatHistory.length,
    interruptedSessions.length,
    lastActiveChatIdFromRedux,
  ])

  // Handle resuming interrupted conversation
  const handleResumeConversation = () => {
    if (pendingResumption) {
      const chat = filteredChatHistory.find(c => c.message_id === pendingResumption.messageId)
      if (chat) {
        handleChatHistoryClick({ preventDefault: () => {} }, chat)
      }
      setShowResumptionBanner(false)
      setPendingResumption(null)
    }
  }

  // Dismiss resumption banner and start fresh
  const handleDismissResumption = () => {
    setShowResumptionBanner(false)
    setPendingResumption(null)
  }

  /**
   * Handle question suggestion clicks
   */
  const handleSuggestionClick = (e, suggestion) => {
    e.preventDefault()
    setComposerValue(suggestion)
    formRef?.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  /**
   * Handle voice input submission
   */
  const handleVoiceSubmit = (transcript: string) => {
    if (!transcript.trim()) return
    setComposerValue(transcript)
    // Enable voice response for voice-triggered questions
    setVoiceResponseEnabled(true)
    // Small delay to ensure form value is set before submit
    setTimeout(() => {
      formRef?.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
    }, 50)
  }

  /**
   * Handle action item clicks from the response renderer
   * This allows users to click on recommended actions and have the AI execute them
   */
  const handleActionClick = (actionNumber: number, actionTitle: string) => {
    console.log('[handleActionClick] Triggered - Action:', actionNumber, 'Title:', actionTitle)

    // Format the message clearly - include title if available
    let message: string
    if (actionTitle && actionTitle.length > 0) {
      // Clean up the title for the message
      const cleanTitle = actionTitle.replace(/[""]/g, '"').trim()
      message = `Execute action #${actionNumber}: ${cleanTitle}`
    } else {
      message = `Execute action #${actionNumber} from the list above.`
    }

    console.log('[handleActionClick] Message:', message)

    // Set the value and submit directly
    setComposerValue(message)

    // Call onSubmit directly with the message (more reliable than form dispatch)
    setTimeout(() => {
      console.log('[handleActionClick] Calling onSubmit...')
      onSubmit({ [gptFieldName]: message })
    }, 100)
  }

  /**
   * Handle new chat - clear current messages
   */
  const handleNewChat = () => {
    // Bound per-chat streaming buckets: keep only chats still streaming; finished
    // chats' content lives in history and reloads on switch. Prevents unbounded growth.
    dispatch(pruneInsightBuckets({ keepIds: activeChatIds }))
    setMessages([])
    setCurrentChatId(null) // Clear current chat ID when starting new chat
    setCurrentConversationId(null)
    setCurrentTurnOffset(0)
    setHasEarlierMessages(false)
    setEarlierMessagesLoading(false)
    setTaskConfigPanelOpen(false)
    setTaskConfigPanelContext(null)
    resetForm()
    // Reset scroll button state
    setShowScrollToBottom(false)
    // Clear streaming state
    setIsStreaming(false)
    setStreamingMessageId(null)
    setLoadingMessage(null)
    // Clear cancel token reference
    cancelTokenRef.current = null
    // Clear agent state (steps, status) and insight stream state for a fresh composer
    dispatch(clearAgentSteps({ sourcePage: 'ai' }))
    dispatch(resetInsightForSourcePage({ sourcePage: 'ai' }))
    setAgentMessageId(null)
    // Reset agent data panel state - close panel on new chat (will reopen when data arrives)
    setAgentDataPanelOpen(false)
    setAgentDataPanelExpanded(false) // Reset expansion state
    // Clear any task library curated workflow context from a previous session
    dispatch(clearActivePreBuiltTaskContext())
    // Focus the input after clearing messages
    setTimeout(() => setFocus(gptFieldName), 100)
  }

  // Handle navigation state from AppSidebar (chat history clicks, new chat).
  // Ref ensures we only process once per navigation even if effect re-runs (e.g. from Redux re-renders).
  const processedNavStateKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!location.state) {
      processedNavStateKeyRef.current = null
      return
    }
    const key = location.key ?? location.pathname
    if (processedNavStateKeyRef.current === key) return
    processedNavStateKeyRef.current = key

    const { selectedChat, action } = location.state as any
    if (!selectedChat && action !== 'new-chat') {
      return
    }

    if (selectedChat) {
      handleChatHistoryClick({ preventDefault: () => {} }, selectedChat)
    } else if (action === 'new-chat') {
      handleNewChat()
    }

    navigate({ pathname: location.pathname, search: '' }, { replace: true, state: null })
  }, [location.pathname, location.key])

  /**
   * Task Library "Chat Now": when /ai is opened with ?preBuiltTaskId=<uuid>, fetch the
   * referenced template, start a fresh chat, prefill the composer with a starter prompt,
   * and stash the task as activePreBuiltTaskContext so onSubmit can forward the task
   * name + objective to askAgentForInsight. Once consumed the URL param is stripped to avoid
   * re-firing on subsequent re-renders.
   */
  const preBuiltTaskIdParam = searchParams.get('preBuiltTaskId')
  const processedPreBuiltTaskIdRef = useRef<string | null>(null)
  const preBuiltTaskAutoStart = Boolean((location.state as any)?.autoStartPreBuiltTask)
  useEffect(() => {
    if (!preBuiltTaskIdParam) return
    const sessionStorageKey = `ai:prebuilt-task-consumed:${preBuiltTaskIdParam}`
    const alreadyConsumedInTab = sessionStorage.getItem(sessionStorageKey) === '1'
    if (!preBuiltTaskAutoStart && alreadyConsumedInTab) return
    if (processedPreBuiltTaskIdRef.current === preBuiltTaskIdParam && !preBuiltTaskAutoStart) return
    processedPreBuiltTaskIdRef.current = preBuiltTaskIdParam

    let cancelled = false
    ;(async () => {
      try {
        const action: any = await dispatch(fetchPreBuiltTaskById(preBuiltTaskIdParam) as any)
        if (cancelled) return
        const task = (action?.payload && typeof action.payload === 'object') ? action.payload : null
        if (!task || !task.id) {
          toast.error('Could not load that Task Library entry.')
        } else {
          sessionStorage.setItem(sessionStorageKey, '1')
          handleNewChat()
          // Default to agent mode so the curated tool pipeline is actually usable.
          if (!agentModeEnabled) {
            dispatch(toggleAgentMode() as any)
          }
          const objective = (task.summary || task.description || '').toString().trim()
          const prefill = [
            `Help me run "${task.name}".`,
            objective ? `Objective: ${objective}` : '',
          ].filter(Boolean).join('\n\n')
          const clearAutoStartState = () => {
            if (!preBuiltTaskAutoStart) return
            navigate(
              { pathname: location.pathname, search: location.search },
              { replace: true, state: null },
            )
          }
          const submitWhenPrefillReady = (attempt = 0) => {
            if (cancelled) return

            const currentValue = (getValues(gptFieldName) || '').trim()
            if (currentValue === prefill.trim()) {
              void handleSubmit(onSubmit)()
              clearAutoStartState()
              return
            }

            if (attempt >= 5) {
              console.warn('[AI Chat] Chat Now prefill was not ready for auto-submit')
              clearAutoStartState()
              return
            }

            setComposerValue(prefill, {
              shouldDirty: true,
              shouldTouch: true,
            })
            setTimeout(() => submitWhenPrefillReady(attempt + 1), 100)
          }

          setComposerValue(prefill, {
            shouldDirty: true,
            shouldTouch: true,
          })
          dispatch(setActivePreBuiltTaskContext(task))
          // Auto-send only after the prefill is actually present in form state.
          setTimeout(() => submitWhenPrefillReady(), 0)
        }
      } finally {
        // URL param intentionally kept so the deeplink remains shareable/bookmarkable.
        // Re-firing is prevented by processedPreBuiltTaskIdRef above.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [preBuiltTaskIdParam, preBuiltTaskAutoStart, location.pathname, location.search])

  const activePreBuiltTaskContext = useSelector(selectActivePreBuiltTaskContext)

  /**
   * Handle stop chat streaming
   */
  const handleStopChat = (e) => {
    e.preventDefault()
    e.stopPropagation()

    // Agent mode — finalize the placeholder bubble, tell server to abort immediately, then cancel the client stream.
    // Include abort-ref so Stop still works if agent mode was toggled off mid-request.
    const agentInFlight =
      insightStatus === 'loading' && (agentModeEnabled || !!agentAbortControllerRef.current)
    if (agentInFlight) {
      const chatId = currentChatId
      if (chatId) {
        axios.post(`${openAIUrl}/chat/agent/stop/${encodeURIComponent(chatId)}`, {}, getAxiosDefaultConfig()).catch(() => {})
        dispatch(markChatIdle(chatId))
      }

      const partial = typeof insightResponse === 'string' ? insightResponse.trim() : ''
      const stoppedContent = partial || 'Response stopped.'

      if (agentMessageId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === agentMessageId
              ? {
                  ...msg,
                  content: stoppedContent,
                  agentStatus: 'cancelled',
                  stoppedByUser: true,
                  agentSteps: [...agentSteps],
                }
              : msg,
          ),
        )
        setAgentMessageId(null)
      }

      agentAbortControllerRef.current?.abort()
      agentAbortControllerRef.current = null
      dispatch(setAgentStatus({ sourcePage: streamKey, status: 'cancelled' }))
      toast.success('Agent stopped')
      return
    }

    // Handle regular chat cancellation
    if (cancelTokenRef.current) {
      // Cancel the current request
      cancelTokenRef.current.cancel('Request cancelled by user')

      // Clear streaming state immediately
      setIsStreaming(false)
      dispatch(markChatIdle(streamingMessageId || currentChatId))
      setStreamingMessageId(null)
      setLoadingMessage(null)
      cancelTokenRef.current = null

      toast.success('Chat stopped successfully')
    }
  }

  /**
   * Handle follow-up response clicks
   */
  // const handleFollowUpResponse = (e, response, messageId) => {
  //   e.preventDefault()
  //   // Handle follow-up response logic here
  //   if (response === 'yes') {
  //     // Handle yes response
  //     // find the message with the given messageId
  //     setValue('generateCustomReport', true)
  //     const message = messages.find(msg => msg.id === messageId)
  //     if (message) {
  //       // Do something with the message
  //       setComposerValue(message?.content || '')
  //       formRef?.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  //     }
  //   } else {
  //     // Handle no response
  //     // Generate a new message to clear the follow-up
  //     setValue('generateCustomReport', false)
  //     const newMessage = {
  //       id: uuidv4(),
  //       author: { picture: ChatGPTLogo, email: 'ChatGPT', },
  //       content: 'Thanks for your response! If you have any other questions, feel free to ask.',
  //     }
  //     setMessages(prevMessages => [...prevMessages, newMessage])
  //   }
  // }

  /**
   *
   * Handle select fields
   *
   */
  const modelOptions = [
    { value: 'gpt-3.5-turbo-1106', label: 'gpt-3.5-turbo-1106' },
    { value: 'gpt-3.5-turbo-16k', label: 'gpt-3.5-turbo-16k' },
    { value: 'gpt-4', label: 'gpt-4' },
    { value: 'gpt-4-32k', label: 'gpt-4-32k' },
  ]

  const currentOpenAIAccount = useSelector(selectOpenAIAccount)
  const currentOpenAIAccountStatus = useSelector(selectOpenAIAccountStatus)
  useEffect(() => {
    if (currentOpenAIAccountStatus === 'idle') {
      dispatch(fetchOpenAIAccount({ key_id: openAIKeyId }))
    }
  }, [currentOpenAIAccountStatus])

  useEffect(() => {
    if (currentOpenAIAccountStatus === 'fulfilled' && currentOpenAIAccount) {
      Object.keys(currentOpenAIAccount.permissions).map((permission) => {
        setValue(permission, true)
      })
    }
  }, [currentOpenAIAccount])

  const onSave = async (permissions) => {
    // Handle permissions
    try {
      if (currentOpenAIAccount?.id) {
        // If openai account already exists, update
        const updatePayload = {
          key_id: openAIKeyId,
          permissions,
        }

        await dispatch(updateOpenAIAccount(updatePayload))
      } else {
        // Otherwise, create
        const createPayload = {
          app_email: user.email,
          key_id: openAIKeyId,
          permissions,
        }

        await dispatch(createOpenAIAccount(createPayload))
      }
    } catch (err) {
      console.log(err)
    }
  }

  const saveReport = async () => {
    try {
      await dispatch(
        saveCustomReport(
          {
            ...saveReportModalData,
            name: saveReportName,
          },
          false,
          true
        )
      )
    } catch (err) {
      console.log(err)
      toast.error('Failed to save custom report.')
    }
  }

  useEffect(() => {
    if (!savedCustomerReportId && customerReportId) {
      setSavedCustomerReportId(customerReportId)
    }
  }, [customerReportId])

  const permissions = _.chain({
    overview: watch('overview'),
    products: watch('products'),
    marketing: watch('marketing'),
    geographics: watch('geographics'),
    customers: watch('customers'),
  })
    .pick(['overview', 'products', 'marketing', 'geographics', 'customers'])
    .toPairs()
    .filter(([k, v]) => v === true)
    .fromPairs()
    .value()

  const addOnOptionConfigs = addOnOptions(addOnPrices)
  const aiAddOnOption = addOnOptionConfigs?.find(a => a.product === 'AI')

  // Disabled for now
  // if (!(addOnEnabled || userCredits > 0) && !addOnUpgradeDismissed && !isAdmin) {
  //   return (
  //     <AIChatUpgradeModal
  //       setDoneAction={setDoneAction}
  //       changeApplication={changeApplication}
  //       isAdmin={isAdmin}
  //       mimicStatus={mimicStatus}
  //       setRedirectedAction={setRedirectedAction}
  //       setAddOnUpgradeDismissed={setAddOnUpgradeDismissed}
  //       aiAddOnOption={aiAddOnOption}
  //       allowContinue={mimicStatus || isAdmin}
  //       prevAddOnNames={prevAddOnNames}
  //     />
  //   )
  // }

  return (
    <div className="tw-relative tw-w-full tw-h-full tw-min-h-0 tw-overflow-hidden tw-flex tw-flex-col tw-bg-white">
      {isDeepDive && deepDiveGlowKeyframes}
      <Modal show={saveReportModalOpen} onHide={() => setSaveReportModalOpen(false)}>
        <Modal.Header>
          <Modal.Title>Save as Custom Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {savedCustomerReportId ? (
            <div>
              <p>
                {`Your report`}&nbsp;<b>{saveReportName}</b>&nbsp;{`has been saved as a custom report.`}
              </p>
              <LinkContainer to={`/custom/${savedCustomerReportId}`}>
                <Button
                  variant="link"
                  onClick={() => {
                    dispatch(changeApplication('ai', 'custom'))
                    navigate(`/custom/${savedCustomerReportId}`)
                  }}
                >
                  View Report
                </Button>
              </LinkContainer>
            </div>
          ) : (
            <div>
              <h2 className="tw-text-xl tw-mb-1">Report Name</h2>
              <Form.Control
                type="text"
                value={saveReportName}
                placeholder="My Custom Report"
                onChange={(e) => setSaveReportName(e.target.value)}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {savedCustomerReportId ? (
            <div className="right full-width">
              <Button
                variant="primary"
                onClick={() => {
                  setSaveReportModalOpen(false)
                  setSaveReportName('')
                  setSaveReportModalData(null)
                  setSavedCustomerReportId(null)
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="right full-width">
              <Button
                variant="secondary"
                className="float-left"
                onClick={() => {
                  setSaveReportModalOpen(false)
                  setSaveReportName('')
                  setSaveReportModalData(null)
                  setSavedCustomerReportId(null)
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={saveReport} disabled={!saveReportName}>
                Save
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>

      {selectedTaskId ? (
        isMobile ? (
          <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-w-full tw-overflow-hidden">
            <div className="tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-hidden tw-flex tw-flex-col">
              <ScheduledTaskDetail
                taskId={selectedTaskId}
                compactActionBar
                initialExpandedRunId={initialScheduledRunFromUrl}
                onBack={() => {
                  setTaskConfigPanelOpen(false)
                  setTaskConfigPanelContext(null)
                  dispatch(setSelectedTaskId(null))
                }}
                onEditTask={(task) => {
                  setTaskConfigPanelContext({
                    editMode: true,
                    initialTask: task,
                    toolName: task.tool_name || '',
                    toolParameters: task.tool_parameters || {},
                  })
                  setTaskConfigPanelOpen(true)
                }}
              />
            </div>
            <Sheet
              open={!!taskConfigPanelOpen && !!taskConfigPanelContext}
              onOpenChange={(open) => {
                if (!open) {
                  setTaskConfigPanelOpen(false)
                  setTaskConfigPanelContext(null)
                }
              }}
            >
              <SheetContent
                side="bottom"
                hideCloseButton
                className="tw-h-[90vh] tw-max-h-[90vh] tw-w-full tw-rounded-t-2xl tw-flex tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0"
                style={{ padding: 0 }}
              >
                <SheetTitle className="tw-sr-only">Edit scheduled task</SheetTitle>
                {taskConfigPanelContext && (
                  <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden">
                    <TaskConfigPanel
                      isOpen={taskConfigPanelOpen}
                      onClose={() => {
                        setTaskConfigPanelOpen(false)
                        setTaskConfigPanelContext(null)
                      }}
                      message={taskConfigPanelContext.message}
                      agentSteps={taskConfigPanelContext.agentSteps}
                      toolName={
                        taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                          ? taskConfigPanelContext.initialTask.tool_name
                          : taskConfigPanelContext.toolName
                      }
                      toolParameters={
                        taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                          ? taskConfigPanelContext.initialTask.tool_parameters || {}
                          : taskConfigPanelContext.toolParameters
                      }
                      conversationHistory={taskConfigPanelContext.conversationHistory ?? []}
                      currentChatId={currentChatId}
                      userEmail={user?.email || ''}
                      keyId={agentKeyId || ''}
                      originalMessageId={taskConfigPanelContext.message?.messageId}
                      editMode={taskConfigPanelContext.editMode}
                      initialTask={taskConfigPanelContext.initialTask}
                      sheetEmbedded
                      isAdmin={isAdmin}
                      isMimic={!!user?.is_mimic}
                    />
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        ) : (
          <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-row tw-overflow-hidden tw-w-full">
            <div className="tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-hidden tw-flex tw-flex-col">
              <ScheduledTaskDetail
                taskId={selectedTaskId}
                compactActionBar={false}
                initialExpandedRunId={initialScheduledRunFromUrl}
                onBack={() => {
                  setTaskConfigPanelOpen(false)
                  setTaskConfigPanelContext(null)
                  dispatch(setSelectedTaskId(null))
                }}
                onEditTask={(task) => {
                  setTaskConfigPanelContext({
                    editMode: true,
                    initialTask: task,
                    toolName: task.tool_name || '',
                    toolParameters: task.tool_parameters || {},
                  })
                  setTaskConfigPanelOpen(true)
                }}
              />
            </div>
            {taskConfigPanelOpen && taskConfigPanelContext && (
              <TaskConfigPanel
                isOpen={taskConfigPanelOpen}
                onClose={() => {
                  setTaskConfigPanelOpen(false)
                  setTaskConfigPanelContext(null)
                }}
                message={taskConfigPanelContext.message}
                agentSteps={taskConfigPanelContext.agentSteps}
                toolName={
                  taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                    ? taskConfigPanelContext.initialTask.tool_name
                    : taskConfigPanelContext.toolName
                }
                toolParameters={
                  taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                    ? taskConfigPanelContext.initialTask.tool_parameters || {}
                    : taskConfigPanelContext.toolParameters
                }
                conversationHistory={taskConfigPanelContext.conversationHistory ?? []}
                currentChatId={currentChatId}
                userEmail={user?.email || ''}
                keyId={agentKeyId || ''}
                originalMessageId={taskConfigPanelContext.message?.messageId}
                editMode={taskConfigPanelContext.editMode}
                initialTask={taskConfigPanelContext.initialTask}
                inlineSidebar
                isAdmin={isAdmin}
                isMimic={!!user?.is_mimic}
              />
            )}
          </div>
        )
      ) : isToolAccessView ? (
        <ToolAccessMainView />
      ) : isChatHistoryView ? (
        <ChatHistoryMainView />
      ) : isCustomToolsMainView ? (
        <CustomToolsMainView />
      ) : isScheduledTasksView ? (
        isMobile ? (
          <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-w-full tw-overflow-hidden">
            <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-12 tw-text-center">
              <h2 className="tw-text-lg tw-font-semibold tw-text-slate-800 tw-mb-2">Scheduled tasks</h2>
              <p className="tw-text-sm tw-text-slate-600 tw-max-w-sm tw-mb-1">
                Choose a task in the sidebar to view runs and settings.
              </p>
              <p className="tw-text-xs tw-text-slate-500 tw-max-w-sm tw-mb-4">
                You can also create a scheduled task anytime—no chat required.
              </p>
              <button
                type="button"
                onClick={openStandaloneTaskConfigPanel}
                className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 tw-transition-colors"
              >
                New scheduled task
              </button>
            </div>
            <Sheet
              open={!!taskConfigPanelOpen && !!taskConfigPanelContext}
              onOpenChange={(open) => {
                if (!open) {
                  setTaskConfigPanelOpen(false)
                  setTaskConfigPanelContext(null)
                }
              }}
            >
              <SheetContent
                side="bottom"
                hideCloseButton
                className="tw-h-[90vh] tw-max-h-[90vh] tw-w-full tw-rounded-t-2xl tw-flex tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0"
                style={{ padding: 0 }}
              >
                <SheetTitle className="tw-sr-only">New scheduled task</SheetTitle>
                {taskConfigPanelContext && (
                  <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden">
                    <TaskConfigPanel
                      isOpen={taskConfigPanelOpen}
                      onClose={() => {
                        setTaskConfigPanelOpen(false)
                        setTaskConfigPanelContext(null)
                      }}
                      message={taskConfigPanelContext.message}
                      agentSteps={taskConfigPanelContext.agentSteps}
                      toolName={
                        taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                          ? taskConfigPanelContext.initialTask.tool_name
                          : taskConfigPanelContext.toolName
                      }
                      toolParameters={
                        taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                          ? taskConfigPanelContext.initialTask.tool_parameters || {}
                          : taskConfigPanelContext.toolParameters
                      }
                      conversationHistory={taskConfigPanelContext.conversationHistory ?? []}
                      currentChatId={currentChatId}
                      userEmail={user?.email || ''}
                      keyId={agentKeyId || ''}
                      originalMessageId={taskConfigPanelContext.message?.messageId}
                      editMode={taskConfigPanelContext.editMode}
                      initialTask={taskConfigPanelContext.initialTask}
                      sheetEmbedded
                      isAdmin={isAdmin}
                      isMimic={!!user?.is_mimic}
                    />
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        ) : (
          <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-row tw-w-full tw-overflow-hidden">
            <div className="tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-auto tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-12 tw-text-center">
              <h2 className="tw-text-lg tw-font-semibold tw-text-slate-800 tw-mb-2">Scheduled tasks</h2>
              <p className="tw-text-sm tw-text-slate-600 tw-max-w-sm tw-mb-1">
                Choose a task in the sidebar to view runs and settings.
              </p>
              <p className="tw-text-xs tw-text-slate-500 tw-max-w-sm tw-mb-4">
                You can also create a scheduled task anytime—no chat required.
              </p>
              <button
                type="button"
                onClick={openStandaloneTaskConfigPanel}
                className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 tw-transition-colors"
              >
                New scheduled task
              </button>
            </div>
            {taskConfigPanelOpen && taskConfigPanelContext && (
              <TaskConfigPanel
                isOpen={taskConfigPanelOpen}
                onClose={() => {
                  setTaskConfigPanelOpen(false)
                  setTaskConfigPanelContext(null)
                }}
                message={taskConfigPanelContext.message}
                agentSteps={taskConfigPanelContext.agentSteps}
                toolName={
                  taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                    ? taskConfigPanelContext.initialTask.tool_name
                    : taskConfigPanelContext.toolName
                }
                toolParameters={
                  taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                    ? taskConfigPanelContext.initialTask.tool_parameters || {}
                    : taskConfigPanelContext.toolParameters
                }
                conversationHistory={taskConfigPanelContext.conversationHistory ?? []}
                currentChatId={currentChatId}
                userEmail={user?.email || ''}
                keyId={agentKeyId || ''}
                originalMessageId={taskConfigPanelContext.message?.messageId}
                editMode={taskConfigPanelContext.editMode}
                initialTask={taskConfigPanelContext.initialTask}
                inlineSidebar
                isAdmin={isAdmin}
                isMimic={!!user?.is_mimic}
              />
            )}
          </div>
        )
      ) : (
        <>
      {/* Sidebar removed - chat history now in main AppSidebar */}

      <div className="tw-w-full tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-hidden tw-relative">

        {activePreBuiltTaskContext && (
          <div className="tw-shrink-0 tw-mx-3 tw-mt-3 tw-mb-1 tw-rounded-lg tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2">
            <div className="tw-flex tw-items-start tw-gap-2">
              <span className="tw-mt-0.5 tw-inline-flex tw-h-5 tw-w-5 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-text-[10px] tw-font-semibold">
                TL
              </span>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                  <span className="tw-text-xs tw-font-semibold tw-text-blue-900">
                    Task context loaded
                  </span>
                  <span className="tw-text-xs tw-text-blue-800 tw-truncate">
                    {activePreBuiltTaskContext.name}
                  </span>
                </div>
                {activePreBuiltTaskContext.objective && (
                  <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-text-slate-700">
                    Objective: {activePreBuiltTaskContext.objective}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dispatch(clearActivePreBuiltTaskContext())}
                className="tw-flex-shrink-0 tw-p-1 tw-text-blue-700 hover:tw-text-blue-900 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                title="Clear task context"
              >
                <span className="fa fa-times" />
              </button>
            </div>
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-hidden">
          {/* Collect results data for the right panel */}
          {(() => {
            const resultsData = messages
              .filter((msg) => !msg.author?.email || msg.author?.email !== user.email)
              .flatMap((msg, msgIdx) => {
                const configs = msg.assistantReportConfig || []
                return configs
                  .filter((config: any) => config?.reportData || config?.videoUrls?.length)
                  .map((config: any) => ({
                    reportData: config.reportData,
                    reportType: config.reportType,
                    videoUrls: config.videoUrls || config.supportVideoUrls,
                    startDate: config.startDate,
                    endDate: config.endDate,
                    report_id: config.report_id,
                  }))
              })
            const hasResults = resultsData.length > 0
            // Check if we should show agent data panel - when search/fetch tools are called (running or completed)
            // Panel shows as soon as any PayHelm search/fetch tool is invoked, even before results arrive
            // IMPORTANT: Don't show panel when there are no messages (fresh chat / welcome screen)
            const hasDataFetchingResults = messages.length > 0 && agentSteps.some(step =>
              step.tool && isDataFetchingTool(step.tool)
            )
            // Also check saved agent steps from previous messages for historical data results
            const historicalAgentSteps = messages
              .filter(msg => msg.isAgent && msg.agentSteps?.length > 0)
              .flatMap(msg => msg.agentSteps || [])
            const hasHistoricalDataResults = historicalAgentSteps.some((step: any) =>
              step.tool && isDataFetchingTool(step.tool) && step.status === 'completed' && hasChartableData(step.toolOutput)
            )
            // Combine live Redux steps with historical steps from messages (prefer live steps if they exist)
            const combinedAgentSteps = agentSteps.length > 0 ? agentSteps : historicalAgentSteps
            const hasAgentData = agentModeEnabled && (hasDataFetchingResults || hasHistoricalDataResults)
            const showReportPanel = !!reportPanel && !taskConfigPanelOpen
            const showAgentDataPanel = hasAgentData && agentDataPanelOpen && !showReportPanel
            const showResultsPanel =
              hasResults && resultsPanelOpen && !showAgentDataPanel && !taskConfigPanelOpen && !showReportPanel
            const showAgentDataExpanded =
              showAgentDataPanel && agentDataPanelExpanded && !taskConfigPanelOpen
            // Check if any side panel is EXPANDED (collapsed panel doesn't count as side panel for layout)
            // On mobile, results and agent data panels are shown in bottom drawers so they don't affect layout
            const hasSidePanel =
              (showAgentDataExpanded && !isMobile) ||
              (showResultsPanel && !isMobile) ||
              (showReportPanel && !isMobile) ||
              (taskConfigPanelOpen && !isMobile)
            // Collapsed 60px strip: data explorer minimized, or task panel open with data (strip shows Data + Task shortcuts)
            const hasCollapsedPanel =
              !isMobile &&
              ((showAgentDataPanel && !agentDataPanelExpanded && !taskConfigPanelOpen) ||
                (taskConfigPanelOpen && showAgentDataPanel))
            // Calculate chat width - use custom width if set, otherwise default percentages
            // When no panel: chat spans full container width. When panel open: chat shares width with panel.
            const getChatWidthStyle = (): React.CSSProperties => {
              if (!hasSidePanel && !hasCollapsedPanel) {
                // No panel - chat spans full width of container
                return { width: '100%', flexShrink: 0 }
              }
              // Task config + optional 60px data strip: share space like expanded data panel
              if (taskConfigPanelOpen && !isMobile) {
                if (chatPanelWidth > 0) {
                  return {
                    width: `${chatPanelWidth}px`,
                    minWidth: `${MIN_CHAT_WIDTH}px`,
                    flexShrink: 0,
                  }
                }
                return { width: '38%', minWidth: '300px', maxWidth: '45%', flexShrink: 0 }
              }
              if (hasCollapsedPanel && !taskConfigPanelOpen) {
                // Only collapsed data strip (no full task panel)
                return { width: 'calc(100% - 60px)', flexShrink: 0 }
              }
              // Use custom width if set and resizing, otherwise use default
              if (chatPanelWidth > 0) {
                return {
                  width: `${chatPanelWidth}px`,
                  minWidth: `${MIN_CHAT_WIDTH}px`,
                  flexShrink: 0
                }
              }
              // Default widths based on Data Explorer panel state (only when expanded)
              if (showAgentDataExpanded || showReportPanel) {
                return { width: '40%', minWidth: '300px', maxWidth: '40%', flexShrink: 0 } // Panel expanded: chat 40%, panel 60%
              }
              return { width: '60%', flexShrink: 0 }
            }

            return (
              <>
              <div className="tw-flex tw-bg-white tw-flex-1 tw-min-h-0 tw-overflow-hidden">
                {/* Chat Area - must have min-width 0 so it can shrink when results panel is open */}
                <div
                  ref={chatAreaRef}
                  className={`tw-flex tw-flex-col tw-min-h-0 tw-min-w-0 tw-overflow-hidden tw-relative ${isResizing ? '' : 'tw-transition-all tw-duration-300'}`}
                  style={{ ...getChatWidthStyle(), minHeight: 0 } as React.CSSProperties}
                >
              {/* Single hidden file input for images and CSV attachments */}
              <input
                type="file"
                ref={attachmentInputRef}
                accept="image/png,image/jpeg,image/gif,image/webp,.csv,.json,.txt,application/json,text/plain,text/csv,application/csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple
                className="tw-hidden"
                onChange={handleAttachmentSelect}
              />

              {/* Resumption Banner - shown when interrupted conversation detected */}
              {showResumptionBanner && pendingResumption && (
                <div className="tw-absolute tw-top-4 tw-left-4 tw-right-4 tw-z-50 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-4 tw-shadow-lg">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-text-amber-500 tw-text-xl">⚠️</span>
                    <div className="tw-flex-1">
                      <h4 className="tw-font-semibold tw-text-amber-800 tw-mb-1">Interrupted Conversation</h4>
                      <p className="tw-text-sm tw-text-amber-700 tw-mb-3">
                        Your last conversation was interrupted: "{pendingResumption.preview?.slice(0, 80)}..."
                      </p>
                      <div className="tw-flex tw-gap-2">
                        <button
                          onClick={handleResumeConversation}
                          className="tw-px-4 tw-py-1.5 tw-bg-amber-500 tw-text-white tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-amber-600 tw-transition-colors"
                        >
                          Resume
                        </button>
                        <button
                          onClick={handleDismissResumption}
                          className="tw-px-4 tw-py-1.5 tw-bg-white tw-text-amber-700 tw-border tw-border-amber-300 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-amber-50 tw-transition-colors"
                        >
                          Start Fresh
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div
                ref={messagesContainerRef}
                style={messages.length === 0 ? undefined : { scrollbarGutter: 'stable' }}
                className={`tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-bg-white ${
                  messages.length === 0
                    ? 'tw-overflow-visible'
                    : 'tw-overflow-y-auto tw-pb-4'
                }`}
              >
                {/* Content wrapper for messages only - welcome screen stays at this level */}
                {messages.length > 0 ? (
                  <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-px-4 xl:tw-px-8">
                    {/* Load earlier messages button — shown when a long conversation is partially loaded */}
                    {hasEarlierMessages && (
                      <div className="tw-flex tw-justify-center tw-py-3">
                        <button
                          onClick={handleLoadEarlierMessages}
                          disabled={earlierMessagesLoading}
                          className="tw-px-4 tw-py-1.5 tw-text-xs tw-text-slate-500 hover:tw-text-slate-700 tw-border tw-border-slate-200 hover:tw-border-slate-300 tw-rounded-full tw-bg-white hover:tw-bg-slate-50 tw-transition-colors tw-flex tw-items-center tw-gap-1.5"
                        >
                          {earlierMessagesLoading ? (
                            <span className="fa fa-spinner fa-spin tw-text-xs"></span>
                          ) : (
                            <span className="fa fa-chevron-up tw-text-xs"></span>
                          )}
                          Load earlier messages
                        </button>
                      </div>
                    )}
                    {messages.map((message, i) => {
                      const {
                        author,
                        content,
                        rating,
                        messageId,
                        messageStatus,
                        showSuggestions,
                        reportConfig = {},
                        ratings = {},
                        assistantReportConfig = [],
                        generatedPdfs = [],
                        isAgent = false,
                        agentSteps: savedAgentSteps = [],
                        agentStatus: savedAgentStatus = 'idle',
                        creditSummary: savedCreditSummary = null,
                      } = message

                      const isUser = author?.email === user.email
                      const isAssistant = !isUser
                      const assistantReportType = assistantReportConfig[i]?.reportType || null
                      const assistantSupportVideoLinks = assistantReportConfig[i]?.videoUrls || assistantReportConfig[i]?.supportVideoUrls || null

                      // Check if this is the current agent message being processed
                      const isCurrentAgentMessage = isAgent && message.id === agentMessageId
                      // For agent messages, use live Redux state if active, otherwise use saved state
                      const displayAgentSteps = isCurrentAgentMessage ? agentSteps : savedAgentSteps
                      const displayAgentStatus = isCurrentAgentMessage ? agentStatus : savedAgentStatus
                      // Show chain of thought only when there is substantive work (tools, actionable plan, etc.)
                      const showChainOfThought = isAgent && (
                        (isCurrentAgentMessage && hasSubstantiveAgentSteps(displayAgentSteps))
                        || (!isCurrentAgentMessage && hasSubstantiveAgentSteps(savedAgentSteps))
                      )
                      // An agent turn that has stopped must never fall through to
                      // the loader — a run that ended without answer text (or a
                      // restored interrupted run) would otherwise spin forever.
                      const agentTurnEnded = isAgent && !isCurrentAgentMessage && isTerminalAgentStatus(displayAgentStatus)

                      // TEMP DIAGNOSTIC — remove once the no-spinner bug is confirmed fixed.
                      if (isAgent) {
                        console.log('[DIAG-STREAM] render agent message', {
                          messageId: message.id,
                          agentMessageId,
                          isCurrentAgentMessage,
                          displayAgentStatus,
                          savedAgentStatusOnMsg: savedAgentStatus,
                          agentTurnEnded,
                          showChainOfThought,
                          content: typeof content === 'string' ? content.slice(0, 60) : content,
                          insightStatus,
                        })
                      }

                      return (
                        <div
                          key={message.id ?? i}
                          className={`tw-flex ${i === 0 ? 'tw-mt-16' : ''} ${
                            isUser ? 'tw-justify-end tw-mb-10' : 'tw-justify-start'
                          } ${showChainOfThought && displayAgentStatus === 'running' ? 'tw-flex-1 tw-min-h-0' : isAssistant ? 'tw-mb-4' : ''}`}
                        >
                          {/* Message Content */}
                          <div className={`${isUser ? 'tw-max-w-[70%] tw-ml-auto' : 'tw-w-full'} ${showChainOfThought && displayAgentStatus === 'running' ? 'tw-flex tw-flex-col tw-flex-1 tw-min-h-0' : ''}`}>
                            {/* User Message Bubble */}
                            {isUser && (
                              <div className="tw-px-4 tw-py-3 tw-rounded-2xl tw-bg-blue-500 tw-text-white tw-rounded-br-md">
                                {message.images?.length > 0 && (
                                  <div className={`tw-flex tw-flex-wrap tw-gap-2 tw-mb-2 ${message.images.length > 1 ? '' : ''}`}>
                                    {message.images.map((img, imgIdx) => (
                                      <img key={imgIdx} src={img.previewUrl} alt="Attached" className={`tw-rounded-lg tw-border tw-border-blue-400/30 ${message.images.length === 1 ? 'tw-max-w-full tw-max-h-48' : 'tw-w-24 tw-h-24 tw-object-cover'}`} />
                                    ))}
                                  </div>
                                )}
                                {message.csvAttachments?.length > 0 && (
                                  <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2">
                                    {message.csvAttachments.map((csv, csvIdx) => (
                                      <div key={`${csv.filename}-${csvIdx}`} className="tw-flex tw-items-center tw-gap-2 tw-bg-white/10 tw-border tw-border-blue-400/30 tw-rounded-lg tw-pl-2 tw-pr-3 tw-py-1.5 tw-text-xs tw-max-w-[200px]">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-md tw-bg-white/20 tw-flex-shrink-0">
                                          <FileTypeIcon filename={csv.filename} className="tw-w-3.5 tw-h-3.5" />
                                        </div>
                                        <div className="tw-flex tw-flex-col tw-min-w-0">
                                          <span className="tw-truncate tw-font-medium">{csv.filename}</span>
                                          <span className="tw-text-[9px] tw-text-blue-100 tw-uppercase tw-tracking-wide">{attachmentTypeLabel(csv.filename)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {message.clips?.length > 0 ? (
                                  <>
                                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2">
                                      {message.clips.map((clip) => (
                                        <PasteClipChip key={clip.id} label={clip.label} text={clip.text} lineCount={clip.lineCount} />
                                      ))}
                                    </div>
                                    {message.typedContent ? <div className="tw-flex tw-items-center">{message.typedContent}</div> : null}
                                  </>
                                ) : (
                                  <div className="tw-flex tw-items-center">{content}</div>
                                )}
                              </div>
                            )}

                            {/* Agent/Assistant Message */}
                            {isAssistant && (
                              <div className={!showChainOfThought ? 'tw-text-gray-900 tw-text-lg' : ''}>
                                {/* Agent Chain of Thought - show ABOVE content when completed, below when running */}
                                {showChainOfThought && displayAgentStatus !== 'running' && (
                                  <AgentChainOfThought
                                    steps={displayAgentSteps}
                                    status={displayAgentStatus}
                                    finalResponse={content !== AGENT_WORKING_PLACEHOLDER ? content : undefined}
                                    memoryUpdate={isCurrentAgentMessage ? agentMemoryUpdate : null}
                                    narrations={isCurrentAgentMessage ? agentNarrations : []}
                                    messageId={currentConversationId || undefined}
                                    creditSummary={savedCreditSummary}
                                  />
                                )}

                                {/* Cross-chat / task knowledge the agent drew on (AI Chat 2.0) */}
                                {isCurrentAgentMessage && knowledgeSources.length > 0 && (
                                  <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                                    <span className="tw-text-xs tw-text-gray-400 tw-mr-1">Drew on:</span>
                                    {knowledgeSources.map((src) => (
                                      <span
                                        key={src.id}
                                        title={src.snippet}
                                        className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-xs tw-rounded-full tw-bg-slate-100 tw-text-slate-600"
                                      >
                                        {src.kind === 'task_run' ? '📋' : '💬'} {src.title}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Response content - for agent messages, show when completed or user-stopped */}
                                {content && content !== AGENT_WORKING_PLACEHOLDER && (!isAgent || !showChainOfThought) ? (
                                  <div>
                                    {/* Agent messages always get the same Answer-card chrome
                                        (green header) as a tool-using turn's AnswerCard, whether
                                        or not this turn called any tools — a plain conversational
                                        reply used to fall through to a bare, unstyled
                                        AgentResponseRenderer here, which looked inconsistent with
                                        every other agent response. hideFooter: the richer action
                                        row below (persisted ratings, voice, report export)
                                        already covers Copy/thumbs for this path. */}
                                    {isAgent ? (
                                      <AnswerCard text={content} onActionClick={handleActionClick} hideFooter />
                                    ) : (
                                      <div className="tw-text-gray-900 tw-text-lg">
                                        <ReactMarkdown
                                          components={markdownComponents}
                                          rehypePlugins={[rehypeRaw, rehypeKatex, remarkGfm]}
                                          remarkPlugins={[[remarkMath, remarkMathOptions], remarkGfm]}
                                        >
                                          {content}
                                        </ReactMarkdown>
                                      </div>
                                    )}
                                    {displayAgentStatus === 'cancelled' && (
                                      <div className="tw-text-xs tw-text-gray-500 tw-mt-2 tw-italic">Response stopped</div>
                                    )}
                                  </div>
                                ) : agentTurnEnded || (!isCurrentAgentMessage && messageStatus) ? null : !showChainOfThought ? (
                                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <BiLoaderAlt className="tw-animate-spin" />
                                    <div style={{ marginLeft: 8 }} className="tw-text-gray-900">
                                      {loadingMessage}
                                    </div>
                                  </div>
                                ) : null}

                                {/* Agent Chain of Thought - show BELOW content when running */}
                                {showChainOfThought && displayAgentStatus === 'running' && (
                                  <AgentChainOfThought
                                    steps={displayAgentSteps}
                                    status={displayAgentStatus}
                                    finalResponse={content !== AGENT_WORKING_PLACEHOLDER ? content : undefined}
                                    memoryUpdate={isCurrentAgentMessage ? agentMemoryUpdate : null}
                                    narrations={isCurrentAgentMessage ? agentNarrations : []}
                                    messageId={currentConversationId || undefined}
                                    creditSummary={savedCreditSummary}
                                  />
                                )}
                                {!!messageStatus && !isCurrentAgentMessage && !showChainOfThought && (
                                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <BiLoaderAlt className="tw-animate-spin" />
                                    {messageStatus !== 'loading' && (
                                      <div style={{ marginLeft: 8 }} className="tw-text-gray-900">
                                        {messageStatus}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Insight Cards for Assistant Messages - only show inline when panel is closed */}
                            {!resultsPanelOpen && isAssistant && assistantReportConfig[i]?.reportData && (
                              <AIInsightCards
                                reportData={assistantReportConfig[i]?.reportData}
                                reportType={assistantReportType}
                              />
                            )}

                            {/* Chart Visualization for Assistant Messages - only show inline when panel is closed */}
                            {!resultsPanelOpen && isAssistant && assistantReportConfig[i]?.reportData && assistantReportType && (
                              <AIChart
                                reportData={assistantReportConfig[i]?.reportData}
                                reportType={assistantReportType}
                              />
                            )}

                            {/* Report Data Table for Assistant Messages - only show inline when panel is closed */}
                            {!resultsPanelOpen && isAssistant && assistantReportType && assistantReportConfig[i]?.reportData && (
                              <ReportDataTable
                                reportType={assistantReportType}
                                maxRows={50}
                                reportData={assistantReportConfig[i]?.reportData}
                              />
                            )}

                            {/* "View report" cards for reports the agent saved (manage_reports / manage_custom_reports) */}
                            {isAssistant && isAgent && (() => {
                              const reportCards = extractAgentReportCards(displayAgentSteps)
                              if (!reportCards.length) return null
                              return (
                                <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-3">
                                  {reportCards.map((card) => (
                                    <AgentReportCard
                                      key={`${card.kind}:${card.id}`}
                                      card={card}
                                      active={reportPanel?.reportId === card.id}
                                      onView={openReportPanel}
                                    />
                                  ))}
                                </div>
                              )
                            })()}

                            {/* Generated PDF download cards (agent-produced report PDFs) —
                                rich inline asset card: branded cover + filename/meta, click to download */}
                            {isAssistant && Array.isArray(generatedPdfs) && generatedPdfs.length > 0 && (
                              <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-3">
                                {generatedPdfs.map((pdf: any) => {
                                  const kb = pdf.size_bytes ? Math.max(1, Math.round(pdf.size_bytes / 1024)) : null
                                  const meta = [
                                    'PDF',
                                    pdf.page_count ? `${pdf.page_count} page${pdf.page_count === 1 ? '' : 's'}` : null,
                                    kb ? `${kb} KB` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')
                                  return (
                                    <button
                                      key={pdf.attachment_id}
                                      type="button"
                                      title={`Download ${pdf.filename || 'PDF'}`}
                                      onClick={async () => {
                                        try {
                                          const res = await axios.get(
                                            `${openAIUrl}/chat/agent/attachments/${pdf.attachment_id}/download`,
                                            { ...getAxiosDefaultConfig(), responseType: 'blob' },
                                          )
                                          const url = window.URL.createObjectURL(res.data)
                                          const a = document.createElement('a')
                                          a.href = url
                                          a.download = pdf.filename || 'report.pdf'
                                          document.body.appendChild(a)
                                          a.click()
                                          a.remove()
                                          window.URL.revokeObjectURL(url)
                                        } catch (e) {
                                          console.error('[AI] PDF download failed', e)
                                        }
                                      }}
                                      className="tw-group tw-w-64 tw-text-left tw-rounded-xl tw-overflow-hidden tw-border tw-border-gray-200 tw-bg-white hover:tw-border-blue-300 hover:tw-shadow-md tw-transition tw-cursor-pointer"
                                    >
                                      {/* Branded cover (faux preview) */}
                                      <div className="tw-relative tw-h-28 tw-px-4 tw-py-3 tw-overflow-hidden tw-bg-gradient-to-br tw-from-blue-600 tw-to-sky-500 tw-flex tw-flex-col tw-justify-between">
                                        <div className="tw-text-white tw-text-sm tw-font-semibold tw-leading-snug tw-overflow-hidden" style={{ maxHeight: '3rem' }}>
                                          {pdf.title || 'Report'}
                                        </div>
                                        <div className="tw-text-blue-100 tw-text-xs tw-uppercase tw-tracking-wide">PayHelm Report</div>
                                        <BiSolidFilePdf className="tw-absolute tw-top-3 tw-right-3 tw-text-white tw-opacity-80" size={18} />
                                      </div>
                                      {/* File meta row */}
                                      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-bg-red-50 tw-flex-shrink-0">
                                          <BiSolidFilePdf className="tw-text-red-500" size={18} />
                                        </div>
                                        <div className="tw-flex-1 tw-min-w-0">
                                          <div className="tw-text-xs tw-font-medium tw-text-gray-900 tw-truncate">{pdf.filename || 'report.pdf'}</div>
                                          <div className="tw-text-xs tw-text-gray-500">{meta}</div>
                                        </div>
                                        <BiDownload className="tw-text-gray-400 group-hover:tw-text-blue-500 tw-flex-shrink-0" size={18} />
                                      </div>
                                    </button>
                                  )
                                })}
                              </div>
                            )}

                            {/* Support Videos for Assistant Messages - only show inline when panel is closed */}
                            {!resultsPanelOpen && isAssistant && assistantSupportVideoLinks && (
                              <SupportVideos videoUrls={assistantSupportVideoLinks} />
                            )}

                            {/* Action Buttons for Assistant Messages - for agent messages, show when completed or stopped.
                                For isAgent, this attaches directly below AnswerCard (hideFooter'd, rounded-top-only,
                                no bottom border above) as that card's bottom section, instead of floating as a
                                separate row below it — otherwise the credit-cost text visually detached from the card. */}
                            {isAssistant && content && content !== AGENT_WORKING_PLACEHOLDER && (!isAgent || !showChainOfThought) && (
                              <div className={isAgent
                                ? 'tw-flex tw-items-center tw-gap-2 tw-mx-3 tw-mb-3 tw-px-3 tw-py-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded-b-lg tw-shadow-[0_1px_6px_rgba(15,23,42,0.06)]'
                                : 'tw-flex tw-items-center tw-gap-2 tw-mt-2'
                              }>
                                {/* Copy Button */}
                                <button
                                  type="button"
                                  className="tw-p-1.5 tw-rounded tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-200 tw-transition-colors"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(content)
                                    toast.success('Message copied to clipboard!')
                                  }}
                                  title="Copy message"
                                >
                                  <span className="fa fa-copy"></span>
                                </button>

                                {/* Play Voice Button */}
                                <VoiceOutput
                                  text={content}
                                  autoPlay={false}
                                  voice="nova"
                                  onError={(error) => toast.error(error)}
                                />

                                {/* Thumbs Up */}
                                <button
                                  type="button"
                                  className={`tw-p-1.5 tw-rounded tw-transition-colors ${
                                    ratings[i]?.rating === true
                                      ? 'tw-text-blue-600 tw-bg-blue-100'
                                      : 'tw-text-gray-500 hover:tw-text-blue-600 hover:tw-bg-blue-50'
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setRating(messageId, ratings[i]?.rating == true ? null : true, i)
                                  }}
                                  title="Good response"
                                >
                                  <span className="fa fa-thumbs-up"></span>
                                </button>

                                {/* Thumbs Down */}
                                <button
                                  type="button"
                                  className={`tw-p-1.5 tw-rounded tw-transition-colors ${
                                    ratings[i]?.rating === false
                                      ? 'tw-text-red-600 tw-bg-red-100'
                                      : 'tw-text-gray-500 hover:tw-text-red-600 hover:tw-bg-red-50'
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setRating(messageId, ratings[i]?.rating === false ? null : false, i)
                                  }}
                                  title="Poor response"
                                >
                                  <span className="fa fa-thumbs-down"></span>
                                </button>

                                {/* View Report Button */}
                                {assistantReportConfig.length > 0 && assistantReportConfig[i] && assistantReportType && (
                                  <>
                                    <LinkContainer
                                      to={{
                                        pathname: `/reports/${assistantReportConfig[i].report_id}`,
                                        search: `?startDate=${assistantReportConfig[i].startDate}&endDate=${assistantReportConfig[i].endDate}`
                                      }}
                                      onClick={() => {
                                        dispatch(changeApplication('ai', 'reports'))
                                      }}
                                    >
                                      <button
                                        type="button"
                                        className="tw-p-1.5 tw-rounded tw-text-gray-600 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-colors"
                                        title="View Generated Report"
                                      >
                                        <span className="fa fa-chart-bar"></span>&nbsp;&nbsp;View Report
                                      </button>
                                    </LinkContainer>

                                    {/* Export Dropdown */}
                                    <Dropdown className="tw-inline-block">
                                      <Dropdown.Toggle
                                        as="button"
                                        type="button"
                                        className="tw-p-1.5 tw-rounded tw-text-gray-600 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-transition-colors tw-border-0 tw-bg-transparent"
                                        title="Export Options"
                                      >
                                        <span className="fa fa-download"></span>
                                      </Dropdown.Toggle>

                                      <Dropdown.Menu style={{ overflow: 'visible', maxHeight: 'none' }}>
                                        <Dropdown.Item
                                          onClick={(e) => {
                                            e.preventDefault()
                                            handleExportAction('email', assistantReportConfig[i].report_id)
                                          }}
                                        >
                                          <span className="fa fa-envelope tw-mr-2 tw-text-blue-600"></span>
                                          Export to Email
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                          onClick={(e) => {
                                            e.preventDefault()
                                            handleExportAction('csv', assistantReportConfig[i].report_id)
                                          }}
                                        >
                                          <span className="fa fa-file-csv tw-mr-2 tw-text-blue-600"></span>
                                          Export to CSV
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                          onClick={(e) => {
                                            e.preventDefault()
                                            handleExportAction('google_drive', assistantReportConfig[i].report_id)
                                          }}
                                        >
                                          <span className="fa fa-hdd tw-mr-2 tw-text-blue-600"></span>
                                          Export to Google Drive
                                        </Dropdown.Item>

                                        <div className="tw-border-t tw-border-gray-200 tw-my-1"></div>

                                        <Dropdown.Item onClick={(e) => {
                                            e.preventDefault()
                                            setScheduleReportModalReportId(assistantReportConfig[i].report_id)
                                          }
                                        }>
                                          <span className="fa fa-calendar tw-mr-2 tw-text-blue-600"></span>
                                          Schedule Report For Export
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </>
                                )}

                                {/* #935: this footer renders exactly when the
                                    chain-of-thought does not, so without the cost
                                    line here a light turn shows no cost at all.
                                    Last child, and the component owns its own
                                    `ml-auto` root — an empty wrapper would still
                                    absorb flex space. */}
                                <MessageCreditCost
                                  summary={savedCreditSummary}
                                  className="tw-ml-auto"
                                />
                              </div>
                            )}

                            {isAgent &&
                              message.id &&
                              displayAgentStatus === 'completed' &&
                              (() => {
                                const sug = taskSuggestions[message.id]
                                const showCard = sug && (sug.loading || (sug.suggest && !sug.dismissed))
                                if (!showCard) return null
                                const scheduleLabel =
                                  sug.schedule === 'hourly'
                                    ? 'Hourly'
                                    : sug.schedule === 'daily'
                                      ? 'Daily'
                                      : sug.schedule === 'monthly'
                                        ? 'Monthly'
                                        : sug.schedule === 'quarterly'
                                          ? 'Quarterly'
                                          : 'Weekly'
                                return (
                                  <TaskSuggestionCard
                                    taskName={sug.taskName}
                                    reason={sug.reason}
                                    scheduleLabel={scheduleLabel}
                                    loading={!!sug.loading}
                                    onDismiss={() => dispatch(dismissTaskSuggestion(message.id))}
                                    onSetUp={() => {
                                      const steps = isCurrentAgentMessage ? agentSteps : savedAgentSteps
                                      const schedulable = getSchedulableStep(steps)
                                      if (!schedulable) return
                                      openInlineTaskConfigPanel({
                                        message,
                                        agentSteps: steps,
                                        conversationHistory: messages,
                                        toolName: schedulable.toolName,
                                        toolParameters: schedulable.toolInput,
                                      })
                                    }}
                                  />
                                )
                              })()}
                          </div>
                        </div>
                      )
                    })}
                    {/* Queued questions rendered inline as pending user messages */}
                    {questionQueue.map((question, idx) => (
                      <div key={`queued-${idx}`} className="tw-flex tw-justify-end tw-mb-4">
                        <div className="tw-max-w-[70%] tw-ml-auto">
                          <div className="tw-flex tw-items-center tw-px-4 tw-py-3 tw-rounded-2xl tw-bg-blue-400 tw-text-white tw-rounded-br-md tw-opacity-60">
                            {question}
                          </div>
                          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-1">
                            <span className="tw-text-xs tw-text-gray-400">Queued</span>
                            <button
                              type="button"
                              onClick={() => setQuestionQueue(prev => prev.filter((_, i) => i !== idx))}
                              className="tw-text-xs tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
                              title="Remove from queue"
                            >
                              <span className="fa fa-times"></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1 ${isMobile ? 'tw-px-3 tw-overflow-y-auto' : 'tw-px-4 tw-overflow-hidden'}`}>
                    <div className={`tw-max-w-2xl tw-w-full tw-text-center ${isMobile ? 'tw-py-4' : ''}`}>
                      {/* PayHelm Logo */}
                      <div className={isMobile ? 'tw-mb-3' : 'tw-mb-6'}>
                        <img
                          src={PayHelmLogo}
                          alt="PayHelm"
                          className={`${isMobile ? 'tw-h-8' : 'tw-h-12'} tw-mx-auto`}
                        />
                      </div>

                      <h1 className={`${isMobile ? 'tw-text-2xl tw-mb-4' : 'tw-text-4xl tw-mb-5'} tw-font-semibold tw-text-gray-900`}>How can we help?</h1>

                      {/* Search input */}
                      {isMobile ? (
                        /* ── Mobile: single box with input+send on top, divider, controls below ── */
                        <div
                          className={`tw-mb-4 tw-border tw-border-gray-300 tw-rounded-xl tw-shadow-sm ${isDeepDive ? '' : 'focus-within:tw-ring-2 focus-within:tw-ring-blue-500 focus-within:tw-border-blue-500'}`}
                          style={isDeepDive ? deepDiveComposerStyle : undefined}
                        >
                          {renderAttachmentStrip('compact')}
                          {/* Top: textarea + send button */}
                          <div className="tw-flex tw-items-end">
                            <textarea
                              {...registerRest}
                              ref={(e) => {
                                registerRef(e)
                                textareaRef.current = e
                              }}
                              name={gptFieldName}
                              rows={1}
                              className="tw-flex-1 tw-px-4 tw-py-3 tw-text-sm tw-border-0 tw-bg-transparent tw-resize-none focus:tw-outline-none"
                              style={{ maxHeight: 200, overflowY: 'hidden' }}
                              placeholder="Ask anything"
                              disabled={openAIChatStatus === 'loading'}
                              onInput={(e) => autoResizeTextarea(e.currentTarget)}
                              onKeyDown={trySubmitComposerOnEnter}
                              onPaste={handleComposerPaste}
                            />
                            <button
                              className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-rounded-full tw-bg-blue-600 tw-border-0 disabled:tw-bg-gray-300 disabled:tw-cursor-default tw-text-white tw-transition-colors tw-mr-2 tw-mb-2 tw-flex-shrink-0"
                              type="button"
                              disabled={showStopInsteadOfSend ? false : (openAIChatStatus === 'loading')}
                              title={showStopInsteadOfSend ? "Stop" : "Send message"}
                              onClick={showStopInsteadOfSend ? handleStopChat : handleSubmit(onSubmit)}
                            >
                              {showStopInsteadOfSend ? (
                                <span className="fa fa-stop tw-text-sm"></span>
                              ) : (
                                <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {/* Divider */}
                          <div className="tw-border-t tw-border-gray-200 tw-mx-3" />
                          {/* Bottom: controls toolbar */}
                          <div className="tw-flex tw-items-center tw-px-3 tw-py-2">
                            <div className="tw-flex tw-items-center tw-gap-1">
                              <button
                                onClick={handleToggleAgentMode}
                                disabled={agentMcpSetupPending}
                                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-rounded-full tw-text-xs tw-font-medium tw-transition-all tw-duration-200 tw-border-0 ${
                                  agentMcpSetupPending ? 'tw-opacity-60 tw-cursor-wait' : 'tw-cursor-pointer'
                                } ${
                                  agentModeEnabled
                                    ? 'tw-bg-blue-100 tw-text-blue-700'
                                    : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'
                                }`}
                                title={agentModeEnabled ? 'Agent mode enabled' : agentMcpSetupPending ? 'Preparing Agent…' : 'Enable Agent mode'}
                                type="button"
                              >
                                <svg className={`tw-w-3.5 tw-h-3.5 ${agentModeEnabled ? 'tw-text-blue-600' : 'tw-text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Agent
                                {agentModeEnabled && <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-blue-600 tw-animate-pulse"></span>}
                              </button>
                              {agentModeEnabled && (
                                <div className="tw-relative" ref={effortMenuRef}>
                                  <button
                                    type="button"
                                    onClick={() => setEffortMenuOpen(!effortMenuOpen)}
                                    className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                      effortMenuOpen ? 'tw-bg-gray-200 tw-text-gray-700' : isDeepDive ? 'tw-bg-fuchsia-50 tw-text-fuchsia-500 hover:tw-bg-fuchsia-100' : 'tw-bg-gray-100 tw-text-gray-400 hover:tw-bg-gray-200'
                                    }`}
                                    title={isDeepDive ? 'Deep Dive — Astra directs, Fable executes' : 'Agent settings'}
                                  >
                                    {isDeepDive ? (
                                      <SubmarineIcon className="tw-w-3.5 tw-h-3.5" />
                                    ) : (
                                      <svg className="tw-w-3.5 tw-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                      </svg>
                                    )}
                                  </button>
                                  {effortMenuOpen && (
                                    <AgentSettingsMenu
                                      effortLevel={effortLevel}
                                      agentModel={agentModel}
                                      imageModel={imageModel}
                                      onSelectEffort={(level) => dispatch(setEffortLevel(level))}
                                      onSelectModel={(key) => dispatch(setAgentModel(key))}
                                      onSelectImageModel={(key) => dispatch(setImageModel(key))}
                                    />
                                  )}
                                </div>
                              )}
                              {agentModeEnabled && (
                                <button
                                  type="button"
                                  onClick={() => attachmentInputRef.current?.click()}
                                  className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                    attachedImages.length > 0 || attachedCsvs.length > 0 ? 'tw-bg-blue-100 tw-text-blue-600' : 'tw-bg-gray-100 tw-text-gray-400 hover:tw-bg-gray-200'
                                  }`}
                                  title="Attach images or CSV files"
                                >
                                  <svg className="tw-w-3.5 tw-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <div className="tw-flex-1" />
                            <VoiceInput
                              onTranscriptReady={(transcript) => setComposerValue(transcript)}
                              onSubmit={handleVoiceSubmit}
                              disabled={isStreaming || isAgentWorking || openAIChatStatus === 'loading'}
                            />
                          </div>
                        </div>
                      ) : (
                        /* ── Desktop layout: everything inside the input bar ── */
                        <div
                          className={`tw-mb-6 tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-bg-white ${isDeepDive ? '' : 'focus-within:tw-ring-2 focus-within:tw-ring-blue-500 focus-within:tw-border-blue-500'}`}
                          style={isDeepDive ? deepDiveComposerStyle : undefined}
                        >
                        {renderAttachmentStrip('inline')}
                        <div className="tw-relative tw-flex tw-items-center">
                          <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-2 tw-gap-1 tw-z-10">
                            <button
                              onClick={handleToggleAgentMode}
                              disabled={agentMcpSetupPending}
                              className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-full tw-text-sm tw-transition-all tw-duration-200 tw-border-0 ${
                                agentMcpSetupPending ? 'tw-opacity-60 tw-cursor-wait' : 'tw-cursor-pointer'
                              } ${
                                agentModeEnabled
                                  ? 'tw-bg-blue-100 tw-text-blue-700'
                                  : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'
                              }`}
                              title={agentModeEnabled ? 'Agent mode enabled' : agentMcpSetupPending ? 'Preparing Agent…' : 'Enable Agent mode'}
                              type="button"
                            >
                              <svg className={`tw-w-3.5 tw-h-3.5 ${agentModeEnabled ? 'tw-text-blue-600' : 'tw-text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span className="tw-font-medium">Agent</span>
                              <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${agentModeEnabled ? 'tw-bg-blue-600 tw-animate-pulse' : 'tw-bg-transparent'}`}></span>
                            </button>
                            {agentModeEnabled && (
                              <div className="tw-relative" ref={effortMenuRef}>
                                <button
                                  type="button"
                                  onClick={() => setEffortMenuOpen(!effortMenuOpen)}
                                  className={`tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                    effortMenuOpen ? 'tw-bg-gray-200 tw-text-gray-700' : isDeepDive ? 'tw-bg-fuchsia-50 tw-text-fuchsia-500 hover:tw-bg-fuchsia-100' : 'tw-bg-transparent tw-text-gray-400 hover:tw-bg-gray-100'
                                  }`}
                                  title={isDeepDive ? 'Deep Dive — Astra directs, Fable executes' : 'Agent settings'}
                                >
                                  {isDeepDive ? (
                                    <SubmarineIcon className="tw-w-3.5 tw-h-3.5" />
                                  ) : (
                                    <svg className="tw-w-3.5 tw-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                  )}
                                </button>
                                {effortMenuOpen && (
                                  <AgentSettingsMenu
                                    effortLevel={effortLevel}
                                    agentModel={agentModel}
                                    imageModel={imageModel}
                                    onSelectEffort={(level) => dispatch(setEffortLevel(level))}
                                    onSelectModel={(key) => dispatch(setAgentModel(key))}
                                    onSelectImageModel={(key) => dispatch(setImageModel(key))}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          <textarea
                            {...registerRest}
                            ref={(e) => {
                              registerRef(e)
                              textareaRef.current = e
                            }}
                            name={gptFieldName}
                            rows={1}
                            className={`tw-w-full tw-pl-36 ${agentModeEnabled ? 'tw-pr-36' : 'tw-pr-28'} tw-py-4 tw-text-lg tw-leading-6 tw-border-0 tw-bg-transparent focus:tw-outline-none tw-resize-none tw-m-0 tw-align-middle`}
                            style={{ maxHeight: 200, overflowY: 'hidden', minHeight: '56px' }}
                            placeholder="Ask anything"
                            disabled={openAIChatStatus === 'loading'}
                            onInput={(e) => autoResizeTextarea(e.currentTarget)}
                            onKeyDown={trySubmitComposerOnEnter}
                            onPaste={handleComposerPaste}
                          />
                          {agentModeEnabled && (
                            <div className="tw-absolute tw-inset-y-0 tw-right-24 tw-flex tw-items-center tw-gap-1">
                              <button
                                type="button"
                                onClick={() => attachmentInputRef.current?.click()}
                                className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                  attachedImages.length > 0 || attachedCsvs.length > 0 ? 'tw-bg-blue-100 tw-text-blue-600' : 'tw-bg-transparent tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600'
                                }`}
                                title="Attach images or CSV files"
                              >
                                <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                              </button>
                            </div>
                          )}
                          <div className={`tw-absolute tw-inset-y-0 ${agentModeEnabled ? 'tw-right-14' : 'tw-right-12'} tw-flex tw-items-center`}>
                            <VoiceInput
                              onTranscriptReady={(transcript) => setComposerValue(transcript)}
                              onSubmit={handleVoiceSubmit}
                              disabled={isStreaming || isAgentWorking || openAIChatStatus === 'loading'}
                            />
                          </div>
                          <button
                            className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-justify-center tw-w-12 tw-cursor-pointer tw-rounded-r-lg tw-bg-transparent tw-border-0 disabled:tw-bg-transparent disabled:tw-cursor-default tw-text-blue-600 disabled:tw-text-gray-300 tw-transition-colors"
                            type="button"
                            disabled={showStopInsteadOfSend ? false : (openAIChatStatus === 'loading')}
                            title={showStopInsteadOfSend ? "Stop" : "Send message"}
                            onClick={showStopInsteadOfSend ? handleStopChat : handleSubmit(onSubmit)}
                          >
                            {showStopInsteadOfSend ? (
                              <span className="fa fa-stop tw-text-xl"></span>
                            ) : (
                              <span className="fa fa-paper-plane tw-text-xl"></span>
                            )}
                          </button>
                        </div>
                        </div>
                      )}

                      {/* Question suggestions grid — hidden on phone-width screens to avoid cramped layout */}
                      {!isPhone && (
                      <div className={`tw-flex tw-flex-wrap ${isMobile ? 'tw-gap-2 tw-px-1' : 'tw-gap-3'} tw-mx-auto tw-justify-center`}>
                        {dynamicSuggestionsStatus === 'loading' ? (
                          Array.from({ length: isMobile ? 3 : 5 }).map((_, idx) => (
                            <div
                              key={idx}
                              className={`${isMobile ? 'tw-h-8' : 'tw-h-9'} tw-rounded-lg tw-bg-gray-100 tw-animate-pulse tw-inline-block`}
                              style={{ width: `${80 + Math.random() * 100}px` }}
                            />
                          ))
                        ) : (
                          suggestionsToShow.map((suggestion, idx) => (
                            <button
                              key={idx}
                              className={`tw-text-left ${isMobile ? 'tw-px-3 tw-py-2 tw-text-xs' : 'tw-p-2 tw-text-sm tw-whitespace-nowrap'} tw-bg-gray-100 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-transition-all tw-duration-200 tw-text-gray-700 hover:tw-text-blue-600`}
                              onClick={(e) => handleSuggestionClick(e, suggestion)}
                              disabled={openAIChatStatus === 'loading'}
                              type="button"
                            >
                              {suggestion}
                            </button>
                          ))
                        )}
                      </div>
                      )}
                    </div>
                    <div className={`tw-text-center tw-text-xs tw-text-gray-500 ${isMobile ? 'tw-mt-3' : 'tw-mt-4'} tw-pb-2 tw-flex-shrink-0`}>
                      PayHelm Chat can make mistakes. We're continuously improving.
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom input - show when there are messages; always visible at bottom of chat column */}
              {messages.length > 0 && (
                <>
                  <div
                    className={`tw-flex tw-items-center tw-flex-shrink-0 tw-bg-white ${isMobile ? 'tw-px-2 tw-py-1.5' : 'tw-px-4 tw-py-2'} tw-border-t tw-border-slate-100 tw-relative tw-z-10`}
                    style={isPhone ? { paddingBottom: 'env(safe-area-inset-bottom)' } : undefined}
                  >
                    {/* Data panel is always visible as side panel when there's agent data - no toggle button needed */}

                    {/* Desktop: Results chip above input */}
                    {!isMobile &&
                      !resultsPanelOpen &&
                      !hasAgentData &&
                      messages.some((msg) =>
                        (msg.assistantReportConfig || []).some((c: any) => c?.reportData || c?.videoUrls?.length)
                      ) && (
                        <button
                          onClick={() => {
                            setTaskConfigPanelOpen(false)
                            setTaskConfigPanelContext(null)
                            setResultsPanelOpen(true)
                          }}
                          className="tw-absolute tw--top-12 tw-right-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-px-4 tw-h-10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-bg-blue-50 hover:tw-border-blue-300 tw-text-gray-600 hover:tw-text-blue-600 tw-z-10"
                          title="Show results panel"
                          type="button"
                        >
                          <span className="fa fa-chart-bar tw-text-sm"></span>
                          <span className="tw-text-sm tw-font-medium">Results</span>
                        </button>
                      )}

                    {/* Mobile: stacked chips above input (Results, Data, Schedule task) */}
                    {isMobile &&
                      ((!resultsPanelOpen &&
                        !hasAgentData &&
                        messages.some((msg) =>
                          (msg.assistantReportConfig || []).some((c: any) => c?.reportData || c?.videoUrls?.length)
                        )) ||
                        (showAgentDataPanel && !agentDataPanelExpanded) ||
                        (agentModeEnabled && !!lastSchedulableForRail && !taskConfigPanelOpen)) && (
                        <div className="tw-absolute tw-right-2 tw-bottom-full tw-mb-2 tw-flex tw-flex-col tw-items-end tw-gap-2 tw-z-10">
                          {!resultsPanelOpen &&
                            !hasAgentData &&
                            messages.some((msg) =>
                              (msg.assistantReportConfig || []).some((c: any) => c?.reportData || c?.videoUrls?.length)
                            ) && (
                              <button
                                onClick={() => {
                                  setTaskConfigPanelOpen(false)
                                  setTaskConfigPanelContext(null)
                                  setAgentDataPanelExpanded(false)
                                  setResultsPanelOpen(true)
                                }}
                                className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-px-3 tw-h-9 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-bg-blue-50 hover:tw-border-blue-300 tw-text-gray-600 hover:tw-text-blue-600"
                                title="Show results panel"
                                type="button"
                              >
                                <span className="fa fa-chart-bar tw-text-sm"></span>
                                <span className="tw-text-sm tw-font-medium">Results</span>
                              </button>
                            )}
                          {showAgentDataPanel && !agentDataPanelExpanded && (
                            <button
                              onClick={() => {
                                setTaskConfigPanelOpen(false)
                                setTaskConfigPanelContext(null)
                                setResultsPanelOpen(false)
                                setAgentDataPanelExpanded(true)
                              }}
                              className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-px-3 tw-h-9 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-bg-blue-50 hover:tw-border-blue-300 tw-text-gray-600 hover:tw-text-blue-600"
                              title="Show Data Explorer"
                              type="button"
                            >
                              <span className="fa fa-database tw-text-sm"></span>
                              <span className="tw-text-sm tw-font-medium">Data</span>
                            </button>
                          )}
                          {agentModeEnabled && lastSchedulableForRail && !taskConfigPanelOpen && (
                            <button
                              type="button"
                              onClick={() =>
                                openInlineTaskConfigPanel({
                                  message: lastSchedulableForRail.message,
                                  agentSteps: lastSchedulableForRail.agentSteps,
                                  toolName: lastSchedulableForRail.toolName,
                                  toolParameters: lastSchedulableForRail.toolParameters,
                                  conversationHistory: messages,
                                })
                              }
                              className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-px-3 tw-h-9 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-bg-purple-50 hover:tw-border-purple-200 tw-text-gray-600 hover:tw-text-purple-700"
                              title="Save as scheduled task"
                            >
                              <span className="fa fa-calendar tw-text-sm"></span>
                              <span className="tw-text-sm tw-font-medium">Schedule</span>
                            </button>
                          )}
                        </div>
                      )}

                    {/* Scroll to bottom button - positioned above input */}
                    <button
                      onClick={(e) => handleScrollToBottom(e)}
                      className={`tw-absolute tw--top-12 tw-left-1/2 tw--translate-x-1/2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 hover:tw-bg-gray-50 tw-text-gray-600 hover:tw-text-gray-800 tw-z-10 ${
                        showScrollToBottom
                          ? 'tw-opacity-100 tw-scale-100 tw-translate-y-0'
                          : 'tw-opacity-0 tw-scale-95 tw-translate-y-2 tw-pointer-events-none'
                      }`}
                      title="Scroll to bottom"
                    >
                      <span className="fa fa-chevron-down tw-text-sm"></span>
                    </button>

                    <div
                      className={`tw-w-full tw-max-w-4xl tw-mx-auto tw-rounded-lg tw-border tw-border-slate-300 tw-shadow-sm tw-bg-white ${isDeepDive ? '' : 'focus-within:tw-ring-2 focus-within:tw-ring-blue-500 focus-within:tw-border-blue-500'}`}
                      style={isDeepDive ? deepDiveComposerStyle : undefined}
                    >
                    {renderAttachmentStrip('compact')}
                    <div className="tw-flex tw-items-center tw-gap-2 tw-pl-3 tw-pr-2 tw-py-1.5">
                      <span className="fa fa-search tw-text-gray-400 tw-text-base tw-flex-shrink-0"></span>
                      <textarea
                        {...registerRest}
                        ref={(e) => {
                          registerRef(e)
                          textareaRef.current = e
                        }}
                        name={gptFieldName}
                        rows={1}
                        className="tw-flex-1 tw-min-w-0 tw-py-2 tw-text-base tw-leading-6 tw-border-0 tw-bg-transparent tw-resize-none tw-m-0 tw-outline-none placeholder:tw-text-gray-400"
                        style={{ maxHeight: 200, overflowY: 'hidden' }}
                        placeholder="Ask anything"
                        disabled={openAIChatStatus === 'loading'}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        onKeyDown={trySubmitComposerOnEnter}
                        onPaste={handleComposerPaste}
                      />
                      <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                        {agentModeEnabled && (
                          <button
                            type="button"
                            onClick={() => attachmentInputRef.current?.click()}
                            className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                              attachedImages.length > 0 || attachedCsvs.length > 0 ? 'tw-bg-blue-100 tw-text-blue-600' : 'tw-bg-transparent tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600'
                            }`}
                            title="Attach images or CSV files"
                          >
                            <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                          </button>
                        )}
                        {agentModeEnabled && (
                          <div className="tw-relative" ref={effortMenuRef}>
                            <button
                              type="button"
                              onClick={() => setEffortMenuOpen(!effortMenuOpen)}
                              className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors ${
                                effortMenuOpen ? 'tw-bg-gray-200 tw-text-gray-700' : isDeepDive ? 'tw-bg-fuchsia-50 tw-text-fuchsia-500 hover:tw-bg-fuchsia-100' : 'tw-bg-transparent tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600'
                              }`}
                              title={`Effort: ${isDeepDive ? 'Deep Dive' : effortLevel <= 1 ? 'Quick' : effortLevel <= 2 ? 'Light' : effortLevel <= 3 ? 'Standard' : effortLevel <= 4 ? 'Deep' : 'Max'}`}
                            >
                              {isDeepDive ? (
                                <SubmarineIcon className="tw-w-4 tw-h-4" />
                              ) : (
                                <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                              )}
                            </button>
                            {effortMenuOpen && (
                              <AgentSettingsMenu
                                align="right"
                                effortLevel={effortLevel}
                                agentModel={agentModel}
                                imageModel={imageModel}
                                onSelectEffort={(level) => dispatch(setEffortLevel(level))}
                                onSelectModel={(key) => dispatch(setAgentModel(key))}
                                onSelectImageModel={(key) => dispatch(setImageModel(key))}
                              />
                            )}
                          </div>
                        )}
                        <VoiceInput
                          onTranscriptReady={(transcript) => setComposerValue(transcript)}
                          onSubmit={handleVoiceSubmit}
                          disabled={isStreaming || isAgentWorking || openAIChatStatus === 'loading'}
                        />
                        <button
                          className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-border-0 tw-transition-colors ${
                            showStopInsteadOfSend
                              ? 'tw-bg-transparent hover:tw-bg-red-50 tw-text-red-500 hover:tw-text-red-600'
                              : 'tw-bg-transparent hover:tw-bg-gray-100 disabled:tw-bg-transparent disabled:hover:tw-bg-transparent disabled:tw-cursor-default tw-text-blue-600 disabled:tw-text-gray-300'
                          }`}
                          type="button"
                          title={showStopInsteadOfSend ? 'Stop' : 'Send message'}
                          disabled={showStopInsteadOfSend ? false : openAIChatStatus === 'loading'}
                          onClick={showStopInsteadOfSend ? handleStopChat : handleSubmit(onSubmit)}
                        >
                          <span className={`fa ${showStopInsteadOfSend ? 'fa-stop' : 'fa-paper-plane'} tw-text-sm`}></span>
                        </button>
                      </div>
                    </div>
                    </div>
                  </div>
                  <div className={`tw-text-center tw-text-gray-500 tw-max-w-4xl tw-mx-auto tw-flex-shrink-0 ${isMobile ? 'tw-text-[10px] tw-py-0.5' : 'tw-text-xs tw-pt-2'}`}>
                    PayHelm Chat can make mistakes. We're continuously improving.
                  </div>
                </>
              )}
            </div>

                {/* Resize Handle - only show when a side panel is open */}
                {hasSidePanel && (
                  <div
                    className={`tw-w-1 tw-h-full tw-cursor-col-resize tw-flex-shrink-0 tw-group tw-relative ${
                      isResizing ? 'tw-bg-blue-500' : 'tw-bg-gray-200 hover:tw-bg-blue-400'
                    } tw-transition-colors`}
                    onMouseDown={startResize}
                    onDoubleClick={resetChatWidth}
                    title="Drag to resize, double-click to reset"
                  >
                    {/* Wider invisible hit area */}
                    <div className="tw-absolute tw-inset-y-0 tw--left-1 tw-w-3 tw-cursor-col-resize" />
                    {/* Visible handle bar - faint by default, more visible on hover */}
                    <div className={`tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-left-1/2 tw--translate-x-1/2 tw-w-1 tw-h-12 tw-rounded-full tw-transition-all ${
                      isResizing
                        ? 'tw-bg-blue-500 tw-opacity-100'
                        : 'tw-bg-gray-400 tw-opacity-40 group-hover:tw-opacity-100 group-hover:tw-bg-blue-400'
                    }`} />
                  </div>
                )}

                {/* Scheduled task panel (desktop): beside chat; Data Explorer rail can sit to the right */}
                {taskConfigPanelOpen && taskConfigPanelContext && !isMobile && (
                  <div className="tw-flex tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-hidden">
                    <TaskConfigPanel
                      isOpen={taskConfigPanelOpen}
                      onClose={() => {
                        setTaskConfigPanelOpen(false)
                        setTaskConfigPanelContext(null)
                      }}
                      message={taskConfigPanelContext.message}
                      agentSteps={taskConfigPanelContext.agentSteps}
                      toolName={
                        taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                          ? taskConfigPanelContext.initialTask.tool_name
                          : taskConfigPanelContext.toolName
                      }
                      toolParameters={
                        taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                          ? taskConfigPanelContext.initialTask.tool_parameters || {}
                          : taskConfigPanelContext.toolParameters
                      }
                      conversationHistory={taskConfigPanelContext.conversationHistory ?? messages}
                      currentChatId={currentChatId}
                      userEmail={user?.email || ''}
                      keyId={agentKeyId || ''}
                      originalMessageId={taskConfigPanelContext.message?.messageId}
                      editMode={taskConfigPanelContext.editMode}
                      initialTask={taskConfigPanelContext.initialTask}
                      inlineSidebar
                      isAdmin={isAdmin}
                      isMimic={!!user?.is_mimic}
                    />
                  </div>
                )}

                {/* Agent Data Panel - desktop only; on mobile shown in bottom drawer below */}
                {showAgentDataPanel && !isMobile && (
                  <AgentDataPanel
                    steps={combinedAgentSteps}
                    status={agentStatus}
                    isExpanded={!taskConfigPanelOpen && agentDataPanelExpanded}
                    onToggleExpand={() => {
                      if (taskConfigPanelOpen) {
                        setTaskConfigPanelOpen(false)
                        setTaskConfigPanelContext(null)
                        setAgentDataPanelExpanded(true)
                      } else {
                        setAgentDataPanelExpanded(!agentDataPanelExpanded)
                      }
                    }}
                    onClose={() => setAgentDataPanelOpen(false)}
                    availableTools={agentAvailableTools}
                    onOpenScheduledTask={
                      lastSchedulableForRail
                        ? () =>
                            openInlineTaskConfigPanel({
                              message: lastSchedulableForRail.message,
                              agentSteps: lastSchedulableForRail.agentSteps,
                              toolName: lastSchedulableForRail.toolName,
                              toolParameters: lastSchedulableForRail.toolParameters,
                              conversationHistory: messages,
                            })
                        : undefined
                    }
                    scheduledTaskShortcutVisible={
                      agentModeEnabled && !!lastSchedulableForRail && !taskConfigPanelOpen
                    }
                  />
                )}

                {/* Report preview panel (desktop): saved report the agent created, date range only */}
                {showReportPanel && reportPanel && !isMobile && (
                  <div className="tw-flex-1 tw-min-h-0 tw-min-w-[300px] tw-overflow-hidden">
                    <AgentReportPanel
                      key={reportPanel.reportId}
                      reportId={reportPanel.reportId}
                      title={reportPanel.title}
                      reportType={reportPanel.reportType}
                      onClose={() => setReportPanel(null)}
                    />
                  </div>
                )}

                {/* Results Panel - desktop only; on mobile shown in bottom drawer below */}
                {showResultsPanel && !isMobile && (
                  <div className="tw-flex-1 tw-min-h-0 tw-min-w-[300px] tw-overflow-hidden">
                    <AgentResultsPanel
                      results={resultsData}
                      currentIndex={Math.min(resultsPanelIndex, resultsData.length - 1)}
                      onIndexChange={setResultsPanelIndex}
                      onClose={() => setResultsPanelOpen(false)}
                    />
                  </div>
                )}
              </div>

              {/* Mobile: Scheduled task as bottom drawer (mutually exclusive with Results / Data) */}
              {isMobile && taskConfigPanelOpen && taskConfigPanelContext && (
                <Sheet
                  open={taskConfigPanelOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      setResultsPanelOpen(false)
                      setAgentDataPanelExpanded(false)
                    } else {
                      setTaskConfigPanelOpen(false)
                      setTaskConfigPanelContext(null)
                    }
                  }}
                >
                  <SheetContent
                    side="bottom"
                    hideCloseButton
                    className="tw-h-[90vh] tw-max-h-[90vh] tw-w-full tw-rounded-t-2xl tw-flex tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0"
                    style={{ padding: 0 }}
                  >
                    <SheetTitle className="tw-sr-only">Save as scheduled task</SheetTitle>
                    <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden">
                      <TaskConfigPanel
                        isOpen={taskConfigPanelOpen}
                        onClose={() => {
                          setTaskConfigPanelOpen(false)
                          setTaskConfigPanelContext(null)
                        }}
                        message={taskConfigPanelContext.message}
                        agentSteps={taskConfigPanelContext.agentSteps}
                        toolName={
                          taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                            ? taskConfigPanelContext.initialTask.tool_name
                            : taskConfigPanelContext.toolName
                        }
                        toolParameters={
                          taskConfigPanelContext.editMode && taskConfigPanelContext.initialTask
                            ? taskConfigPanelContext.initialTask.tool_parameters || {}
                            : taskConfigPanelContext.toolParameters
                        }
                        conversationHistory={taskConfigPanelContext.conversationHistory ?? messages}
                        currentChatId={currentChatId}
                        userEmail={user?.email || ''}
                        keyId={agentKeyId || ''}
                        originalMessageId={taskConfigPanelContext.message?.messageId}
                        editMode={taskConfigPanelContext.editMode}
                        initialTask={taskConfigPanelContext.initialTask}
                        sheetEmbedded
                        isAdmin={isAdmin}
                        isMimic={!!user?.is_mimic}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              )}

              {/* Mobile: Results panel as bottom drawer */}
              {isMobile && hasResults && (
                <Sheet
                  open={resultsPanelOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      setTaskConfigPanelOpen(false)
                      setTaskConfigPanelContext(null)
                      setAgentDataPanelExpanded(false)
                    } else {
                      setResultsPanelOpen(false)
                    }
                  }}
                >
                  <SheetContent
                    side="bottom"
                    hideCloseButton
                    className="tw-h-[85vh] tw-max-h-[85vh] tw-w-full tw-rounded-t-2xl tw-flex tw-flex-col tw-gap-0 tw-overflow-hidden"
                    style={{ padding: 0 }}
                  >
                    <SheetTitle className="tw-sr-only">Results</SheetTitle>
                    <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden">
                      <AgentResultsPanel
                        results={resultsData}
                        currentIndex={Math.min(resultsPanelIndex, resultsData.length - 1)}
                        onIndexChange={setResultsPanelIndex}
                        onClose={() => setResultsPanelOpen(false)}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              )}

              {/* Mobile: report preview as bottom drawer */}
              {isMobile && showReportPanel && reportPanel && (
                <Sheet open onOpenChange={(open) => { if (!open) setReportPanel(null) }}>
                  <SheetContent
                    side="bottom"
                    hideCloseButton
                    className="tw-h-[90vh] tw-max-h-[90vh] tw-w-full tw-rounded-t-2xl tw-flex tw-flex-col tw-gap-0 tw-overflow-hidden"
                    style={{ padding: 0 }}
                  >
                    <SheetTitle className="tw-sr-only">Report preview</SheetTitle>
                    <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden">
                      <AgentReportPanel
                        key={reportPanel.reportId}
                        reportId={reportPanel.reportId}
                        title={reportPanel.title}
                        reportType={reportPanel.reportType}
                        onClose={() => setReportPanel(null)}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              )}

              {/* Mobile: Agent Data Explorer as bottom drawer */}
              {isMobile && showAgentDataPanel && (
                <Sheet
                  open={agentDataPanelExpanded}
                  onOpenChange={(open) => {
                    if (open) {
                      setTaskConfigPanelOpen(false)
                      setTaskConfigPanelContext(null)
                      setResultsPanelOpen(false)
                    } else {
                      setAgentDataPanelExpanded(false)
                    }
                  }}
                >
                  <SheetContent
                    side="bottom"
                    hideCloseButton
                    className="tw-h-[85vh] tw-max-h-[85vh] tw-w-full tw-rounded-t-2xl tw-flex tw-flex-col tw-gap-0 tw-overflow-hidden"
                    style={{ padding: 0 }}
                  >
                    <SheetTitle className="tw-sr-only">Data Explorer</SheetTitle>
                    <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden">
                      <AgentDataPanel
                        steps={combinedAgentSteps}
                        status={agentStatus}
                        isExpanded={true}
                        onToggleExpand={() => setAgentDataPanelExpanded(false)}
                        onClose={() => setAgentDataPanelExpanded(false)}
                        availableTools={agentAvailableTools}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              )}
            </>
            )
          })()}
        </form>
      </div>
        </>
      )}

      {/* Hidden Voice Output for auto-playing responses (only when voice input was used) */}
      {voiceResponseEnabled && lastCompletedResponse && (
        <div className="tw-hidden">
          <VoiceOutput
            text={lastCompletedResponse}
            autoPlay={true}
            voice="nova"
            onPlayEnd={() => {
              setLastCompletedResponse(null)
              setVoiceResponseEnabled(false) // Disable voice response after playback
            }}
            onError={(error) => {
              console.error('[VoiceOutput Error]', error)
              setLastCompletedResponse(null)
              setVoiceResponseEnabled(false) // Disable voice response on error
            }}
            showAnimation={false}
          />
        </div>
      )}

      <ScheduleReportModal
        isOpen={!!scheduleReportModalReportId}
        onClose={() => setScheduleReportModalReportId(null)}
        reportId={scheduleReportModalReportId}
        googleDriveAcc={googleDriveAccounts}
        toast={toast}
      />
    </div>
  )
}

const mapStateToProps = (store) => ({
  accounts: store.root.accounts,
  balance: store.plan.customerBalance,
  isOwner: store.root.isOwner,
  googleDriveAccounts: store.root.accounts.filter((a) => a.channel === 'Google Drive'),
  addOnEnabled: store.plan.addOns?.find(a => a.plans[0].product === 'AI'),
  userCredits: store.plan.userCredits?.ai || 0,
  addOnUpgradeDismissed: !!store.plan.addOnUpgradeDismissed.AI,
  addOnPrices: store.plan.addOnPrices,
  isAdmin: store.root.admin,
  mimicStatus: store.mimic.mimic.status,
  prevAddOnNames: store.plan.prevAddOnNames,
})

export default connect(mapStateToProps, {
  setDoneAction,
  setRedirectedAction,
  setAddOnUpgradeDismissed,
})(AI)